import { useEffect, useState, useCallback, useMemo } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { signal } from '@preact/signals';
import { api } from '../api/client';
import { tenantName } from '../components/TenantLogo';
import { user } from '../stores/auth.store';
import { useTabRoute } from '../hooks/useTabRoute';
import { SkeletonBar, PageShell, Input as UiInput, Toggle as UiToggle, MetricCard, Button, Select, Modal, TabBar as UiTabBar, Spinner, Badge, StatusBadge as UIStatusBadge, Textarea } from '../components/ui';
import { T } from '../styles/tokens';
import { TwoFactorSetup as TwoFactorSetupSection } from '../components/TwoFactorSetup';

// Types 

interface ProfileData {
 id: string;
 email: string;
 name: string | null;
 company: string | null;
 phone: string | null;
 timezone: string;
 avatar_url: string | null;
 jarvis_voice: string;
 role: string;
 created_at: string;
 updated_at: string;
}

interface NotificationPrefs {
 all_disabled: boolean;
 push_enabled: boolean;
 email_enabled: boolean;
 chat_enabled: boolean;
 marketing_enabled: boolean;
 system_enabled: boolean;
}

interface TokenInfo {
 id: string;
 prefix: string;
 masked: string;
 name: string;
 created_at: string;
 last_used_at: string | null;
 budget_used: number;
 budget_limit: number;
}

interface RouterUsage {
 requests_month: number;
 tokens_in: number;
 tokens_out: number;
 cost_estimate: number;
 models_used: string[];
 last_used_at: string | null;
}

interface UsageSummary {
 today: { tokens: number; calls: number; cost_usd: number };
 month: { tokens: number; calls: number; cost_usd: number };
 daily: Array<{ day: string; tokens: number; calls: number; cost_usd: number }>;
 token_info: TokenInfo | null;
 tokens: TokenInfo[];
 usage: RouterUsage;
}

interface ProofLog {
 id: string;
 type: string;
 tokens: number;
 cost_usd: number;
 project: string | null;
 task: string | null;
 model: string | null;
 created_at: string;
}

interface ProofResponse {
 logs: ProofLog[];
 total: number;
 page: number;
 limit: number;
 pages: number;
 summary: { tokens: number; calls: number; cost_usd: number; first_date: string | null; last_date: string | null };
}

// Signals 

const profile = signal<ProfileData | null>(null);
const notifications = signal<NotificationPrefs>({
 all_disabled: false,
 push_enabled: true,
 email_enabled: true,
 chat_enabled: true,
 marketing_enabled: true,
 system_enabled: true,
});
const loading = signal(true);
const saving = signal(false);
const savingNotifs = signal(false);
const saveMsg = signal('');
const saveNotifsMsg = signal('');
const activeTab = signal<'geral' | 'router' | 'wallet' | 'plano' | 'vault' | 'api'>('geral');

// Form fields
const formName = signal('');
const formCompany = signal('');
const formPhone = signal('');
const formTimezone = signal('America/Sao_Paulo');
const formVoice = signal('default');
const formLanguage = signal('');

// Integration signals
interface ProfileIntegration {
 provider: string;
 name: string;
 icon: string;
 description: string;
 status: 'connected' | 'partial' | 'disconnected';
 account: string | null;
 lastSync: string | null;
 keysConfigured: number;
 keysRequired: number;
 keys: Array<{ key_name: string; preview: string; updated_at: string }>;
}

const integrationsData = signal<ProfileIntegration[]>([]);
const integrationsLoading = signal(false);
const integrationsSearch = signal('');
const disconnectModal = signal<ProfileIntegration | null>(null);
const disconnecting = signal(false);
const oauthMsg = signal<{ type: 'success' | 'error'; text: string } | null>(null);

// LLM Usage signals
const usageData = signal<UsageSummary | null>(null);
const usageLoading = signal(false);

// Proof signals
const proofData = signal<ProofResponse | null>(null);
const proofLoading = signal(false);
const proofPage = signal(1);
const proofPeriod = signal<'month' | 'quarter' | 'year'>('month');
const proofFilterType = signal('');
const proofFilterProject = signal('');

// Constants 

// Monthly limit for the status bar (configurable)
const MONTHLY_TOKEN_LIMIT = 0; // 0 = ilimitado

const JARVIS_VOICES = [
 { id: 'default', name: i18next.t('common:profile.voice_jarvis'), desc: i18next.t('common:profile.voice_jarvis_desc'), emoji: '' },
 { id: 'elon-musk', name: 'Elon Musk', desc: i18next.t('common:profile.voice_elon_desc'), emoji: '' },
 { id: 'deep-narrator', name: i18next.t('common:profile.voice_narrator'), desc: i18next.t('common:profile.voice_narrator_desc'), emoji: '' },
 { id: 'friendly-coach', name: i18next.t('common:profile.voice_coach'), desc: i18next.t('common:profile.voice_coach_desc'), emoji: '' },
];

const TIMEZONES = [
 'America/Sao_Paulo', 'America/New_York', 'America/Chicago', 'America/Denver',
 'America/Los_Angeles', 'America/Manaus', 'America/Fortaleza', 'America/Recife',
 'America/Belem', 'America/Cuiaba', 'America/Campo_Grande', 'America/Porto_Velho',
 'America/Rio_Branco', 'America/Noronha', 'America/Argentina/Buenos_Aires',
 'America/Bogota', 'America/Lima', 'America/Santiago', 'America/Mexico_City',
 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Lisbon', 'Europe/Madrid',
 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore',
 'Australia/Sydney', 'Pacific/Auckland', 'UTC',
];

// Styles 

const inputStyle = {
 width: '100%', padding: '10px 14px', borderRadius: 10,
 background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
 color: T.text, fontSize: 14, outline: 'none', fontFamily: 'inherit',
 transition: 'border-color 0.2s',
};

const labelStyle = {
 fontSize: 13, fontWeight: 600 as const, color: T.textSecondary, marginBottom: 6, display: 'block',
};

const cardStyle = {
 background: T.bgCard, border: '1px solid rgba(255,255,255,0.06)',
 borderRadius: 14, padding: '24px', marginBottom: 20,
};

// API Functions 

async function fetchProfile() {
 loading.value = true;
 try {
 const data = await api.get<{ profile: ProfileData; notifications: NotificationPrefs }>('/profile');
 profile.value = data.profile;
 notifications.value = data.notifications;
 formName.value = data.profile.name || '';
 formCompany.value = data.profile.company || '';
 formPhone.value = data.profile.phone || '';
 formTimezone.value = data.profile.timezone || 'America/Sao_Paulo';
 formVoice.value = data.profile.jarvis_voice || 'default';
 formLanguage.value = data.profile.language || '';
 } catch {
 // ignore
 } finally {
 loading.value = false;
 }
}

async function saveProfile() {
 saving.value = true;
 saveMsg.value = '';
 try {
 await api.put('/profile', {
 name: formName.value,
 company: formCompany.value,
 phone: formPhone.value,
 timezone: formTimezone.value,
 language: formLanguage.value || undefined,
 jarvis_voice: formVoice.value,
 });
 saveMsg.value = ' Perfil salvo com sucesso!';
 if (user.value) {
 user.value = { ...user.value, name: formName.value || null };
 }
 setTimeout(() => { saveMsg.value = ''; }, 3000);
 } catch {
 saveMsg.value = ' Erro ao salvar perfil';
 } finally {
 saving.value = false;
 }
}

async function updateNotification(key: keyof NotificationPrefs, value: boolean) {
 savingNotifs.value = true;
 saveNotifsMsg.value = '';
 try {
 const update: Partial<NotificationPrefs> = { [key]: value };
 if (key === 'all_disabled') {
 update.all_disabled = value;
 if (value) {
 update.push_enabled = false;
 update.email_enabled = false;
 update.chat_enabled = false;
 update.marketing_enabled = false;
 update.system_enabled = false;
 }
 }
 await api.put('/profile/notifications', update);
 notifications.value = { ...notifications.value, ...update };
 saveNotifsMsg.value = ' Preferências atualizadas!';
 setTimeout(() => { saveNotifsMsg.value = ''; }, 2000);
 } catch {
 saveNotifsMsg.value = ' Erro ao atualizar';
 } finally {
 savingNotifs.value = false;
 }
}

async function fetchIntegrations() {
 integrationsLoading.value = true;
 try {
 const data = await api.get<{ integrations: ProfileIntegration[] }>('/profile/integrations');
 integrationsData.value = data.integrations;
 } catch {
 // ignore
 } finally {
 integrationsLoading.value = false;
 }
}

async function disconnectIntegration(provider: string) {
 disconnecting.value = true;
 try {
 // Delete all keys for this provider
 const integration = integrationsData.value.find(i => i.provider === provider);
 if (integration) {
 for (const key of integration.keys) {
 await api.delete(`/integrations/${provider}/${key.key_name}`);
 }
 }
 disconnectModal.value = null;
 await fetchIntegrations();
 } catch {
 // ignore
 } finally {
 disconnecting.value = false;
 }
}

async function fetchUsage() {
 usageLoading.value = true;
 try {
 const data = await api.get<UsageSummary>('/cloud/llm-usage');
 usageData.value = data;
 } catch {
 // ignore
 } finally {
 usageLoading.value = false;
 }
}

function getProofDateRange(): { from: string; to: string } {
 const now = new Date();
 const to = now.toISOString().slice(0, 10);
 let from: string;
 if (proofPeriod.value === 'quarter') {
 const d = new Date(now);
 d.setMonth(d.getMonth() - 3);
 from = d.toISOString().slice(0, 10);
 } else if (proofPeriod.value === 'year') {
 from = `${now.getFullYear()}-01-01`;
 } else {
 from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
 }
 return { from, to };
}

async function fetchProof(page = 1) {
 proofLoading.value = true;
 try {
 const { from, to } = getProofDateRange();
 let qs = `?page=${page}&limit=50&from=${from}&to=${to}`;
 if (proofFilterType.value) qs += `&type=${proofFilterType.value}`;
 if (proofFilterProject.value) qs += `&project=${encodeURIComponent(proofFilterProject.value)}`;
 const data = await api.get<ProofResponse>(`/cloud/llm-usage/proof${qs}`);
 proofData.value = data;
 proofPage.value = page;
 } catch {
 // ignore
 } finally {
 proofLoading.value = false;
 }
}

function downloadCsv() {
 const { from, to } = getProofDateRange();
 let qs = `?from=${from}&to=${to}`;
 if (proofFilterType.value) qs += `&type=${proofFilterType.value}`;
 if (proofFilterProject.value) qs += `&project=${encodeURIComponent(proofFilterProject.value)}`;
 window.open(`/api/v1/cloud/llm-usage/proof/csv${qs}`, '_blank');
}

// Helper Components 

function ToggleSwitch({ enabled, onToggle, disabled }: { enabled: boolean; onToggle: () => void; disabled?: boolean }) {
 return (
 <div
 onClick={disabled ? undefined : onToggle}
 style={{
 width: 44, height: 24, borderRadius: 12, position: 'relative' as const,
 background: enabled ? T.primary : T.borderHover,
 cursor: disabled ? 'not-allowed' : 'pointer',
 transition: 'background 0.2s', flexShrink: 0,
 opacity: disabled ? 0.5 : 1,
 }}
 >
 <div style={{
 width: 18, height: 18, borderRadius: '50%', background: '#fff',
 position: 'absolute' as const, top: 3, left: enabled ? 23 : 3,
 transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
 }} />
 </div>
 );
}

function NotifRow({ label, desc, enabled, onToggle, disabled }: {
 label: string; desc: string; enabled: boolean; onToggle: () => void; disabled?: boolean;
}) {
 return (
 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
 <div style={{ flex: 1 }}>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{label}</div>
 <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>{desc}</div>
 </div>
 <ToggleSwitch enabled={enabled} onToggle={onToggle} disabled={disabled} />
 </div>
 );
}

function formatDate(iso: string): string {
 return new Date(iso).toLocaleString('pt-BR', {
 day: '2-digit', month: '2-digit', year: 'numeric',
 hour: '2-digit', minute: '2-digit',
 });
}

function formatDateShort(iso: string): string {
 return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function getInitials(name: string | null, email: string): string {
 if (name) {
 return name.split(' ').filter(w => w.length > 0).map(w => w.charAt(0)).slice(0, 2).join('').toUpperCase();
 }
 return email.charAt(0).toUpperCase();
}

function fmtUsd(v: number): string {
 return `$${v.toFixed(4)}`;
}

function fmtTokens(v: number): string {
 if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
 if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
 return String(v);
}

// Stat Card 

function StatCard({ emoji, label, value, sub }: { emoji: string; label: string; value: string; sub?: string }) {
 return (
 <div style={{
 background: T.bgCard, border: '1px solid rgba(255,255,255,0.06)',
 borderRadius: 12, padding: '16px', flex: '1 1 0', minWidth: 140,
 }}>
 <div style={{ fontSize: 20, marginBottom: 4 }}>{emoji}</div>
 <div style={{ fontSize: 11, color: T.textSecondary, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>{label}</div>
 <div style={{ fontSize: 22, fontWeight: 800, color: T.text, marginTop: 4 }}>{value}</div>
 {sub && <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>{sub}</div>}
 </div>
 );
}

// SVG Line Chart 

function UsageChart({ daily }: { daily: UsageSummary['daily'] }) {
 if (!daily || daily.length === 0) {
 return <div style={{ color: T.textSecondary, fontSize: 13, padding: 20, textAlign: 'center' as const }}>Sem dados nos últimos 30 dias</div>;
 }

 const W = 600, H = 200, PAD = 40;
 const chartW = W - PAD * 2;
 const chartH = H - PAD * 2;
 const maxTokens = Math.max(...daily.map(d => d.tokens), 1);

 const points = daily.map((d, i) => {
 const x = PAD + (i / Math.max(daily.length - 1, 1)) * chartW;
 const y = PAD + chartH - (d.tokens / maxTokens) * chartH;
 return { x, y, ...d };
 });

 const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
 const lastPt = points[points.length - 1]!;
 const firstPt = points[0]!;
 const areaPath = linePath + ` L${lastPt.x.toFixed(1)},${PAD + chartH} L${firstPt.x.toFixed(1)},${PAD + chartH} Z`;

 // Y-axis labels
 const ySteps = 4;
 const yLabels = Array.from({ length: ySteps + 1 }, (_, i) => {
 const val = (maxTokens / ySteps) * i;
 const y = PAD + chartH - (val / maxTokens) * chartH;
 return { val, y };
 });

 // X-axis labels (show ~6)
 const xStep = Math.max(1, Math.floor(daily.length / 6));
 const xLabels = daily.filter((_, i) => i % xStep === 0 || i === daily.length - 1);

 return (
 <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxHeight: 220 }}>
 {/* Grid lines */}
 {yLabels.map((yl, i) => (
 <g key={i}>
 <line x1={PAD} y1={yl.y} x2={W - PAD} y2={yl.y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
 <text x={PAD - 6} y={yl.y + 4} fill="#A7B3C7" fontSize="9" textAnchor="end">{fmtTokens(yl.val)}</text>
 </g>
 ))}
 {/* Area */}
 <path d={areaPath} fill="url(#chartGrad)" />
 {/* Line */}
 <path d={linePath} fill="none" stroke="#2F6BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
 {/* Dots */}
 {points.map((p, i) => (
 <circle key={i} cx={p.x} cy={p.y} r="3" fill="#2F6BFF" stroke="#0A0F1C" strokeWidth="1.5" />
 ))}
 {/* X labels */}
 {xLabels.map((d, i) => {
 const idx = daily.indexOf(d);
 const x = PAD + (idx / Math.max(daily.length - 1, 1)) * chartW;
 return <text key={i} x={x} y={H - 8} fill="#A7B3C7" fontSize="9" textAnchor="middle">{formatDateShort(d.day)}</text>;
 })}
 <defs>
 <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
 <stop offset="0%" stopColor="#2F6BFF" stopOpacity="0.25" />
 <stop offset="100%" stopColor="#2F6BFF" stopOpacity="0" />
 </linearGradient>
 </defs>
 </svg>
 );
}

// Limit Bar 

function LimitBar({ used, total }: { used: number; total: number }) {
 // 0 = ilimitado
 if (total <= 0) {
 return (
 <div style={{ marginTop: 8 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>
 <span>Uso do mês: {fmtTokens(used)} tokens</span>
 <span style={{ color: T.success, fontWeight: 700 }}>∞ Ilimitado</span>
 </div>
 <div style={{ width: '100%', height: 8, borderRadius: 4, background: T.border }}>
 <div style={{ width: '0%', height: '100%', borderRadius: 4, background: T.success }} />
 </div>
 </div>
 );
 }
 const pct = Math.min(100, (used / total) * 100);
 const color = pct < 60 ? T.success : pct < 80 ? T.warning : T.danger;
 return (
 <div style={{ marginTop: 8 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>
 <span>Uso do mês: {fmtTokens(used)} / {fmtTokens(total)} tokens</span>
 <span style={{ color, fontWeight: 700 }}>{pct.toFixed(1)}%</span>
 </div>
 <div style={{ width: '100%', height: 8, borderRadius: 4, background: T.border }}>
 <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: color, transition: 'width 0.5s' }} />
 </div>
 </div>
 );
}

// Tabs 

/** Check if a plan feature is enabled (JSON features field) */
function hasPlanFeature(feature: string): boolean {
  try {
    const plan = planData.value?.plan;
    if (!plan?.features) return false;
    const f = JSON.parse(plan.features);
    return !!f[feature];
  } catch { return false; }
}

const TABS: Array<{ id: typeof activeTab.value; label: string; emoji: string; requireFeature?: string }> = [
 { id: 'geral', label: i18next.t('common:profile.general'), emoji: '' },
 { id: 'plano', label: i18next.t('common:profile.plan'), emoji: '' },
 { id: 'wallet', label: i18next.t('common:profile.wallet'), emoji: '' },
 { id: 'router', label: i18next.t('common:profile.router'), emoji: '' },
 { id: 'vault', label: i18next.t('common:profile.vault'), emoji: '' },
 { id: 'api', label: 'API', emoji: '' },
];

function TabBar() {
 return (
 <div style={{
 display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid rgba(255,255,255,0.06)',
 overflowX: 'auto' as const, flexWrap: 'nowrap' as const,
 }}>
 {TABS.filter(t => !t.requireFeature || hasPlanFeature(t.requireFeature)).map(t => {
 const active = activeTab.value === t.id;
 return (
 <Button variant="ghost"
 key={t.id}
 onClick={() => {
 activeTab.value = t.id;
 if (t.id === 'integrations' && integrationsData.value.length === 0) fetchIntegrations();
 if (t.id === 'router' || t.id === 'wallet') fetchUsage();
 if (t.id === 'wallet') fetchWalletData();
 /* proof tab removed */
 }}
 style={{
 padding: '10px 16px', border: 'none', background: 'none',
 color: active ? T.primary : T.textSecondary, fontSize: 13, fontWeight: active ? 700 : 500,
 cursor: 'pointer', borderBottom: active ? '2px solid #2F6BFF' : '2px solid transparent',
 transition: 'all 0.2s', whiteSpace: 'nowrap' as const, fontFamily: 'inherit',
 }}
 >
 {t.emoji ? `${t.emoji} ` : ''}{t.label}
 </Button>
 );
 })}
 </div>
 );
}

// Tab: Integrações 

const PROFILE_STATUS_MAP: Record<string, { variant: 'online' | 'warning' | 'offline'; label: string }> = {
 connected: { variant: 'online', label: i18next.t('common:profile.connected') },
 partial: { variant: 'warning', label: i18next.t('common:profile.partial') },
 disconnected: { variant: 'offline', label: i18next.t('common:profile.disconnected') },
};

function StatusBadge({ status }: { status: 'connected' | 'partial' | 'disconnected' }) {
 const cfg = PROFILE_STATUS_MAP[status] || PROFILE_STATUS_MAP.disconnected;
 return <UIStatusBadge variant={cfg.variant} label={cfg.label} size="sm" />;
}

function IntegrationCard({ item }: { item: ProfileIntegration }) {
 return (
 <div style={{
 background: T.bgCard, border: '1px solid rgba(255,255,255,0.06)',
 borderRadius: 14, padding: '20px', display: 'flex', flexDirection: 'column' as const,
 gap: 12, transition: 'border-color 0.2s',
 }}>
 {/* Header */}
 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
 <span style={{ fontSize: 28 }}>{item.icon}</span>
 <div>
 <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{item.name}</div>
 <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>{item.description}</div>
 </div>
 </div>
 <StatusBadge status={item.status} />
 </div>

 {/* Info */}
 {item.status !== 'disconnected' && (
 <div style={{ fontSize: 12, color: T.textSecondary, display: 'flex', flexDirection: 'column' as const, gap: 4 }}>
 {item.account && (
 <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
 <span style={{ color: T.textMuted }}>Conta:</span>
 <span style={{ color: T.text, fontFamily: 'monospace', fontSize: 11 }}>{item.account}</span>
 </div>
 )}
 <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
 <span style={{ color: T.textMuted }}>Chaves:</span>
 <span style={{ color: T.text }}>{item.keysConfigured}/{item.keysRequired} configuradas</span>
 </div>
 {item.lastSync && (
 <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
 <span style={{ color: T.textMuted }}>Atualizado:</span>
 <span style={{ color: T.text }}>{formatDate(item.lastSync)}</span>
 </div>
 )}
 </div>
 )}

 {/* Actions */}
 <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
 {item.status !== 'disconnected' ? (
 <>
 <Button variant="ghost"
 onClick={() => { window.location.href = '/integrations'; }}
 style={{
 flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(47,107,255,0.3)',
 background: 'rgba(47,107,255,0.08)', color: T.primary, fontSize: 12, fontWeight: 600,
 cursor: 'pointer', fontFamily: 'inherit',
 }}
 >
 Gerenciar
 </Button>
 <Button variant="ghost"
 onClick={() => { disconnectModal.value = item; }}
 style={{
 padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)',
 background: 'rgba(239,68,68,0.06)', color: T.danger, fontSize: 12, fontWeight: 600,
 cursor: 'pointer', fontFamily: 'inherit',
 }}
 >
 Desconectar
 </Button>
 </>
 ) : (
 <>
 {item.provider === 'github' ? (
 <Button variant="ghost"
 onClick={() => { window.location.href = '/api/v1/integrations/github/oauth'; }}
 style={{
 flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none',
 background: T.bgSecondary, color: T.text, fontSize: 12, fontWeight: 600,
 cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center',
 justifyContent: 'center', gap: 8,
 }}
 >
 <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
 Conectar com GitHub
 </Button>
 ) : (
 <Button variant="ghost"
 onClick={() => { window.location.href = '/integrations'; }}
 style={{
 flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
 background: 'rgba(255,255,255,0.04)', color: T.textSecondary, fontSize: 12, fontWeight: 600,
 cursor: 'pointer', fontFamily: 'inherit',
 }}
 >
 Conectar
 </Button>
 )}
 </>
 )}
 </div>
 </div>
 );
}

function DisconnectModal() {
 const item = disconnectModal.value;
 if (!item) return null;

 return (
 <Modal open={!!item} onClose={() => { if (!disconnecting.value) disconnectModal.value = null; }} title={`Desconectar ${item.name}?`} size="md">
 <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
 <span style={{ fontSize: 32 }}>{item.icon}</span>
 <div>
 <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>
 Todas as chaves salvas para esta integração serão removidas.
 </div>
 </div>
 </div>
 <div style={{
 padding: '12px 16px', borderRadius: 10, marginBottom: 20,
 background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
 fontSize: 12, color: T.danger, lineHeight: 1.5,
 }}>
 Esta ação não pode ser desfeita. Funcionalidades que dependem desta integração deixarão de funcionar.
 </div>
 <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
 <Button variant="ghost" onClick={() => { disconnectModal.value = null; }} disabled={disconnecting.value}>
 Cancelar
 </Button>
 <Button variant="danger" onClick={() => disconnectIntegration(item.provider)} disabled={disconnecting.value}>
 {disconnecting.value ? i18next.t('common:profile.removing') : i18next.t('common:profile.yes_disconnect')}
 </Button>
 </div>
 </Modal>
 );
}

function TabIntegrations() {
 useEffect(() => { if (integrationsData.value.length === 0) fetchIntegrations(); }, []);
 if (integrationsLoading.value && integrationsData.value.length === 0) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);

 const search = integrationsSearch.value.toLowerCase();
 const filtered = integrationsData.value.filter(i =>
 !search || i.name.toLowerCase().includes(search) || i.provider.toLowerCase().includes(search) || i.description.toLowerCase().includes(search)
 );

 const connected = filtered.filter(i => i.status === 'connected' || i.status === 'partial');
 const disconnected = filtered.filter(i => i.status === 'disconnected');

 return (
 <>
 {/* OAuth Message */}
 {oauthMsg.value && (
 <div style={{
 padding: '12px 16px', borderRadius: 10, marginBottom: 16,
 background: oauthMsg.value.type === 'success' ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
 border: `1px solid ${oauthMsg.value.type === 'success' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
 color: oauthMsg.value.type === 'success' ? T.success : T.danger,
 fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
 }}>
 <span>{oauthMsg.value.text}</span>
 <span onClick={() => { oauthMsg.value = null; }} style={{ cursor: 'pointer', fontSize: 16, opacity: 0.7 }}>x</span>
 </div>
 )}

 {/* Search */}
 <div style={{ marginBottom: 20 }}>
 <input
 type="text"
 placeholder=" Buscar integrações..."
 value={integrationsSearch.value}
 onInput={(e) => { integrationsSearch.value = (e.target as HTMLInputElement).value; }}
 style={{ ...inputStyle, maxWidth: 400 }}
 />
 </div>

 {/* Summary */}
 <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' as const }}>
 <div style={{
 padding: '12px 20px', borderRadius: 12,
 background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)',
 fontSize: 13, color: T.success, fontWeight: 600,
 }}>
 {integrationsData.value.filter(i => i.status === 'connected').length} conectadas
 </div>
 <div style={{
 padding: '12px 20px', borderRadius: 12,
 background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
 fontSize: 13, color: T.warning, fontWeight: 600,
 }}>
 {integrationsData.value.filter(i => i.status === 'partial').length} parciais
 </div>
 <div style={{
 padding: '12px 20px', borderRadius: 12,
 background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
 fontSize: 13, color: T.textSecondary, fontWeight: 600,
 }}>
 {integrationsData.value.filter(i => i.status === 'disconnected').length} disponíveis
 </div>
 </div>

 {filtered.length === 0 ? (
 <div style={{
 ...cardStyle, textAlign: 'center' as const, padding: '48px 24px',
 }}>
 <div style={{ fontSize: 48, marginBottom: 12 }}></div>
 <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8 }}>
 {search ? i18next.t('common:profile.no_integration_found') : i18next.t('common:profile.no_integration_available')}
 </div>
 <div style={{ fontSize: 13, color: T.textSecondary }}>
 {search ? `Nenhum resultado para "${search}"` : i18next.t('common:profile.configure_integrations')}
 </div>
 </div>
 ) : (
 <>
 {/* Connected */}
 {connected.length > 0 && (
 <>
 <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}> Conectadas</h3>
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
 {connected.map(i => <IntegrationCard key={i.provider} item={i} />)}
 </div>
 </>
 )}

 {/* Disconnected */}
 {disconnected.length > 0 && (
 <>
 <h3 style={{ fontSize: 14, fontWeight: 700, color: T.textSecondary, marginBottom: 12 }}> Disponíveis</h3>
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
 {disconnected.map(i => <IntegrationCard key={i.provider} item={i} />)}
 </div>
 </>
 )}
 </>
 )}

 <DisconnectModal />
 </>
 );
}

// ── Password Section (inside Geral tab) ──

function PasswordSection() {
  const { t } = useTranslation('common');
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get<{ hasPassword: boolean }>('/auth/has-password');
        setHasPassword(r.hasPassword);
      } catch { setHasPassword(false); }
    })();
  }, []);

  const handleSave = async () => {
    setPwError(''); setPwSuccess('');
    if (newPw.length < 8) { setPwError(i18next.t('common:profile.password_min_chars')); return; }
    if (newPw !== confirmPw) { setPwError(i18next.t('common:profile.passwords_mismatch')); return; }
    setSaving(true);
    try {
      await api.post('/auth/set-password', { password: newPw });
      setPwSuccess(i18next.t('common:profile.password_saved'));
      setHasPassword(true);
      setShowForm(false);
      setNewPw(''); setConfirmPw('');
    } catch (e: unknown) {
      setPwError((e as { message?: string })?.message || i18next.t('common:profile.password_save_error'));
    }
    setSaving(false);
  };

  const cs: Record<string, string | number> = { background: T.bgInput, borderRadius: 12, border: '1px solid rgba(255,255,255,0.08)', padding: 20, marginBottom: 16 };
  const is2: Record<string, string | number> = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #374151', background: T.bg, color: T.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={cs}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c5cfc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        Login com Senha
      </h2>

      {hasPassword === null ? (
        <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
      ) : hasPassword && !showForm ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 11, color: T.success, background: 'rgba(16,185,129,0.08)', padding: '3px 10px', borderRadius: 6, fontWeight: 600 }}>Senha cadastrada</span>
            <span style={{ fontSize: 12, color: T.textMuted }}>Você pode fazer login com email e senha</span>
          </div>
          {pwSuccess && <div style={{ fontSize: 13, color: T.success, marginBottom: 8 }}>{pwSuccess}</div>}
          <Button variant="ghost" onClick={() => setShowForm(true)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #374151', background: 'rgba(255,255,255,0.08)', color: T.text, fontSize: 13, cursor: 'pointer' }}>
            Alterar senha
          </Button>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>
            {hasPassword ? i18next.t('common:profile.enter_new_password') : i18next.t('common:profile.register_password')}
          </p>
          {pwError && <div style={{ fontSize: 13, color: T.danger, marginBottom: 8, padding: '8px 12px', background: '#EF444415', borderRadius: 8 }}>{pwError}</div>}
          {pwSuccess && <div style={{ fontSize: 13, color: T.success, marginBottom: 8 }}>{pwSuccess}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 400 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: T.textSecondary, marginBottom: 4, fontWeight: 600 }}>Nova senha</label>
              <input type="password" value={newPw} onInput={(e) => setNewPw((e.target as HTMLInputElement).value)} placeholder="Mínimo 8 caracteres" style={is2} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: T.textSecondary, marginBottom: 4, fontWeight: 600 }}>Confirmar senha</label>
              <input type="password" value={confirmPw} onInput={(e) => setConfirmPw((e.target as HTMLInputElement).value)} placeholder="Repita a senha" style={is2}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? t('common:processing') : t('common:profile.save_password')}</Button>
              {showForm && (
                <Button variant="ghost" onClick={() => { setShowForm(false); setPwError(''); }}>Cancelar</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Tab: Geral 

function TabGeral() {
  const { t } = useTranslation('common');
 const p = profile.value!;

 return (
 <>
 {/* Profile Form */}
 <div style={cardStyle}>
 <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
 <span></span> Informações Pessoais
 </h2>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
 <div>
 <label style={labelStyle}>Nome completo</label>
 <input type="text" value={formName.value} onInput={(e) => { formName.value = (e.target as HTMLInputElement).value; }} placeholder="Seu nome" style={inputStyle} />
 </div>
 <div>
 <label style={labelStyle}>Empresa</label>
 <input type="text" value={formCompany.value} onInput={(e) => { formCompany.value = (e.target as HTMLInputElement).value; }} placeholder="Nome da empresa" style={inputStyle} />
 </div>
 <div>
 <label style={labelStyle}>Telefone</label>
 <input type="tel" value={formPhone.value} onInput={(e) => { formPhone.value = (e.target as HTMLInputElement).value; }} placeholder="+55 (11) 99999-9999" style={inputStyle} />
 </div>
 <div>
 <label style={labelStyle}>Fuso horário</label>
 <Select value={formTimezone.value} onChange={(v) => { formTimezone.value = v; }} options={TIMEZONES.map(tz => ({ value: tz, label: tz.replace(/_/g, ' ') }))} />
 </div>
 <div>
 <label style={labelStyle}>🌐 Idioma</label>
 <Select value={formLanguage.value} onChange={(v) => { formLanguage.value = v; }} options={[
   { value: '', label: 'Automático (detectar)' },
   { value: 'pt-BR', label: '🇧🇷 Português (Brasil)' },
   { value: 'en-US', label: '🇺🇸 English (US)' },
   { value: 'es-ES', label: '🇪🇸 Español (España)' },
   { value: 'fr-FR', label: '🇫🇷 Français (France)' },
   { value: 'de-DE', label: '🇩🇪 Deutsch (Deutschland)' },
 ]} />
 </div>
 </div>
 <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20 }}>
 <Button variant="primary" onClick={saveProfile} disabled={saving.value}>
 {saving.value ? t('common:processing') : t('common:profile.save_profile')}
 </Button>
 {saveMsg.value && (
 <span style={{ fontSize: 13, color: saveMsg.value.toLowerCase().includes('salvo') || saveMsg.value.toLowerCase().includes('success') ? T.success : T.danger }}>{saveMsg.value}</span>
 )}
 </div>
 </div>

 {/* Voice Selection */}
 <div style={cardStyle}>
 <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
 <span></span> Voz do Jarvis
 </h2>
 <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16 }}>
 Escolha a personalidade e estilo de comunicação do seu assistente.
 </p>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
 {JARVIS_VOICES.map(v => {
 const selected = formVoice.value === v.id;
 return (
 <div key={v.id} onClick={() => { formVoice.value = v.id; }} style={{
 padding: '16px', borderRadius: 12, cursor: 'pointer',
 background: selected ? 'rgba(47,107,255,0.12)' : 'rgba(255,255,255,0.02)',
 border: `2px solid ${selected ? T.primary : T.border}`,
 transition: 'all 0.2s',
 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
 <span style={{ fontSize: 24 }}>{v.emoji}</span>
 <span style={{ fontSize: 14, fontWeight: 700, color: selected ? T.primary : T.text }}>{v.name}</span>
 </div>
 <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.4 }}>{v.desc}</div>
 {selected && <div style={{ marginTop: 8, fontSize: 11, color: T.primary, fontWeight: 600 }}> Selecionado</div>}
 </div>
 );
 })}
 </div>
 </div>

 {/* Account Info */}
 <div style={cardStyle}>
 <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
 <span></span>Informações da Conta
 </h2>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
 <div>
 <span style={{ color: T.textSecondary }}>Email:</span>
 <span style={{ color: T.text, marginLeft: 8 }}>{profile.value!.email}</span>
 </div>
 <div>
 <span style={{ color: T.textSecondary }}>Plano:</span>
 <span style={{ color: T.primary, marginLeft: 8, fontWeight: 600 }}>{profile.value!.role === 'ADMIN' ? t('common:profile.admin') : t('common:profile.client')}</span>
 </div>
 <div>
 <span style={{ color: T.textSecondary }}>ID:</span>
 <span style={{ color: 'rgba(167,179,199,0.5)', marginLeft: 8, fontSize: 11, fontFamily: 'monospace' }}>{profile.value!.id}</span>
 </div>
 <div>
 <span style={{ color: T.textSecondary }}>Membro desde:</span>
 <span style={{ color: T.text, marginLeft: 8 }}>{formatDate(profile.value!.created_at)}</span>
 </div>
 </div>
 </div>

 {/* Password Setup */}
 <PasswordSection />

 {/* 2FA Setup */}
 <TwoFactorSetupSection />

 {/* Programa de Afiliados */}
 <AffiliateProfileCard />

 {/* Preferências de Notificação (movido de Configurações) */}
 {(() => {
  const n = notifications.value;
  const allOff = n.all_disabled;
  return (
  <div style={cardStyle}>
  <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
  <span>🔔</span> Preferências de Notificação
  </h2>
  <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16 }}>
  Controle quais notificações você deseja receber. Desative todas de uma vez ou configure individualmente.
  </p>
  <div style={{
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 16px', borderRadius: 10, marginBottom: 16,
  background: allOff ? 'rgba(239,68,68,0.08)' : 'rgba(47,107,255,0.08)',
  border: `1px solid ${allOff ? 'rgba(239,68,68,0.2)' : 'rgba(47,107,255,0.2)'}`,
  }}>
  <div>
  <div style={{ fontSize: 14, fontWeight: 700, color: allOff ? T.danger : T.primary }}>
  {allOff ? '🔇 Todas as notificações DESATIVADAS' : '🔔 Notificações ativadas'}
  </div>
  <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>
  {allOff ? t('common:profile.enable_notif') : t('common:profile.disable_notif')}
  </div>
  </div>
  <ToggleSwitch enabled={!allOff} onToggle={() => updateNotification('all_disabled', !allOff)} />
  </div>
  <NotifRow label="Push (navegador)" desc="Notificações push no navegador e dispositivos móveis" enabled={n.push_enabled && !allOff} onToggle={() => updateNotification('push_enabled', !n.push_enabled)} disabled={allOff} />
  <NotifRow label="Email" desc="Notificações por email sobre tarefas e atualizações" enabled={n.email_enabled && !allOff} onToggle={() => updateNotification('email_enabled', !n.email_enabled)} disabled={allOff} />
  <NotifRow label="Chat" desc="Notificações de novas mensagens no chat" enabled={n.chat_enabled && !allOff} onToggle={() => updateNotification('chat_enabled', !n.chat_enabled)} disabled={allOff} />
  <NotifRow label="Marketing" desc="Novidades, promoções e atualizações de produto" enabled={n.marketing_enabled && !allOff} onToggle={() => updateNotification('marketing_enabled', !n.marketing_enabled)} disabled={allOff} />
  <NotifRow label="Sistema" desc="Alertas de segurança, manutenção e status do sistema" enabled={n.system_enabled && !allOff} onToggle={() => updateNotification('system_enabled', !n.system_enabled)} disabled={allOff} />
  {saveNotifsMsg.value && (
  <div style={{ fontSize: 13, color: saveNotifsMsg.value.toLowerCase().includes('salvo') || saveNotifsMsg.value.toLowerCase().includes('success') ? T.success : T.danger, marginTop: 12 }}>
  {saveNotifsMsg.value}
  </div>
  )}
  </div>
  );
 })()}
 </>
 );
}

// ── Affiliate Profile Card ──

function AffiliateProfileCard() {
  const { t } = useTranslation('common');
  const [status, setStatus] = useState<'loading' | 'none' | 'pending' | 'approved' | 'rejected'>('loading');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<any>('/affiliates/me');
        const aff = res?.affiliate || res?.data?.affiliate;
        if (aff) setStatus(aff.status);
        else setStatus('none');
      } catch { setStatus('none'); }
    })();
  }, []);

  if (status === 'loading') return null;

  const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
    approved: { label: i18next.t('common:profile.approved'), color: T.success, bg: 'rgba(16,185,129,0.12)' },
    pending: { label: i18next.t('common:profile.in_review'), color: T.warning, bg: 'rgba(245,158,11,0.12)' },
    rejected: { label: i18next.t('common:profile.rejected'), color: T.danger, bg: 'rgba(239,68,68,0.12)' },
    none: { label: '', color: '', bg: '' },
  };

  const cfg = statusConfig[status] || statusConfig.none;

  return (
    <div style={cardStyle}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🤝</span> Programa de Afiliados
      </h2>
      {status === 'none' ? (
        <div>
          <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 12 }}>
            Ganhe comissões indicando novos clientes para o {tenantName()}.
          </p>
          <a href="/affiliates" style={{
            display: 'inline-block', padding: '10px 20px', borderRadius: 10,
            background: 'linear-gradient(135deg, #7C3AED, #3B82F6)',
            color: T.text, fontWeight: 700, fontSize: 13, textDecoration: 'none',
          }}>
            Torne-se um afiliado
          </a>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: cfg.bg, color: cfg.color,
            }}>
              {cfg.label}
            </span>
            <span style={{ fontSize: 13, color: T.textSecondary }}>
              {status === 'approved' ? t('common:profile.affiliate_active') : status === 'pending' ? t('common:profile.awaiting_approval') : t('common:profile.request_denied')}
            </span>
          </div>
          <a href="/affiliates" style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(47,107,255,0.3)',
            background: 'rgba(47,107,255,0.08)', color: T.primary, fontSize: 12, fontWeight: 600,
            textDecoration: 'none',
          }}>
            {status === 'approved' ? t('common:profile.access_panel') : t('common:profile.see_details')}
          </a>
        </div>
      )}
    </div>
  );
}

// Tab: Elon Router 

// Customer router token (from /api/v1/router/tokens)
interface CustomerRouterToken {
 id: string;
 token_prefix: string;
 customer_id: string;
 machine_id: string | null;
 name: string;
 scopes: string[];
 rate_limit_rpm: number;
 budget_limit_monthly: number;
 budget_used_monthly: number;
 is_active: boolean;
 last_used_at: string | null;
 created_at: string;
 expires_at: string | null;
 revoked_at: string | null;
 total_requests?: number;
 total_cost?: number;
 requests_today?: number;
 cost_today?: number;
 budget_pct?: number;
 status?: 'active' | 'revoked' | 'budget_exceeded' | 'expired';
 is_system_token?: boolean | number;
}

const customerTokens = signal<CustomerRouterToken[]>([]);
const customerTokensLoading = signal(false);
const showCreateTokenModal = signal(false);
const newTokenPlaintext = signal('');
const editingTokenId = signal<string | null>(null);

async function fetchCustomerTokens() {
 customerTokensLoading.value = true;
 try {
 const res = await api.get<{ tokens: CustomerRouterToken[] }>('/router/tokens');
 customerTokens.value = res.tokens || [];
 } catch (e) {
 console.error('Failed to fetch tokens:', e);
 customerTokens.value = [];
 } finally {
 customerTokensLoading.value = false;
 }
}

function TokenStatusBadge({ status }: { status: string }) {
 const map: Record<string, { variant: 'success' | 'error' | 'warning' | 'neutral'; label: string }> = {
 active: { variant: 'success', label: i18next.t('common:profile.active') },
 revoked: { variant: 'error', label: i18next.t('common:profile.revoked') },
 budget_exceeded: { variant: 'warning', label: i18next.t('common:profile.budget_exceeded') },
 expired: { variant: 'neutral', label: i18next.t('common:profile.expired') },
 };
 const s = map[status] ?? map.active;
 return <Badge variant={s.variant}>{s.label}</Badge>;
}

function CreateTokenModal({ onClose }: { onClose: () => void }) {
 const [name, setName] = useState('');
 const [budget, setBudget] = useState(10);
 const [rpm, setRpm] = useState(60);
 const [creating, setCreating] = useState(false);

 const handleCreate = async () => {
 if (!name.trim()) return;
 setCreating(true);
 try {
 const res = await api.post<{ token: CustomerRouterToken; plaintext_token: string }>('/router/tokens', {
 name: name.trim(), budget_limit_monthly: budget, rate_limit_rpm: rpm,
 });
 newTokenPlaintext.value = res.plaintext_token;
 await fetchCustomerTokens();
 onClose();
 } catch (e: any) {
 alert(e.message || i18next.t('common:profile.token_create_error'));
 } finally { setCreating(false); }
 };

 return (
 <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'transparent', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
 onClick={(e: any) => { if (e.target === e.currentTarget) onClose(); }}>
 <div style={{ background: T.bg, borderRadius: 20, padding: 32, border: `1px solid ${T.border}`, width: '100%', maxWidth: 460 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
 <h2 style={{ fontSize: 18, fontWeight: 800, color: T.text, margin: 0 }}> Novo Token</h2>
 <Button variant="ghost" onClick={onClose} style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 20, cursor: 'pointer' }}></Button>
 </div>

 <div style={{ marginBottom: 16 }}>
 <label style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, display: 'block', marginBottom: 6 }}>Nome</label>
 <input value={name} onInput={(e: any) => setName(e.target.value)} placeholder="Ex: Máquina Prod 1"
 style={{ width: '100%', padding: '12px 16px', borderRadius: 10, fontSize: 14, background: T.bgInput, border: `1px solid ${T.border}`, color: T.text, outline: 'none', boxSizing: 'border-box' as const }} />
 </div>

 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
 <div>
 <label style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, display: 'block', marginBottom: 6 }}>Budget Mensal ($)</label>
 <input type="number" value={budget} onInput={(e: any) => setBudget(Number(e.target.value))} min={0}
 style={{ width: '100%', padding: '12px 16px', borderRadius: 10, fontSize: 14, background: T.bgInput, border: `1px solid ${T.border}`, color: T.text, outline: 'none', boxSizing: 'border-box' as const }} />
 </div>
 <div>
 <label style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, display: 'block', marginBottom: 6 }}>RPM Limit</label>
 <input type="number" value={rpm} onInput={(e: any) => setRpm(Number(e.target.value))} min={1}
 style={{ width: '100%', padding: '12px 16px', borderRadius: 10, fontSize: 14, background: T.bgInput, border: `1px solid ${T.border}`, color: T.text, outline: 'none', boxSizing: 'border-box' as const }} />
 </div>
 </div>

 <div style={{ display: 'flex', gap: 12 }}>
 <Button variant="primary" onClick={handleCreate} disabled={creating || !name.trim()} style={{ flex: 1 }}>{creating ? ' Criando...' : ' Criar Token'}</Button>
 <Button variant="ghost" onClick={onClose}>Cancelar</Button>
 </div>
 </div>
 </div>
 );
}

function EditTokenInline({ token, onDone }: { token: CustomerRouterToken; onDone: () => void }) {
 const [name, setName] = useState(token.name);
 const [budget, setBudget] = useState(token.budget_limit_monthly);
 const [saving, setSaving] = useState(false);

 const handleSave = async () => {
 setSaving(true);
 try {
 await api.put(`/router/tokens/${token.id}`, { name: name.trim(), budget_limit_monthly: budget });
 await fetchCustomerTokens();
 onDone();
 } catch (e: any) { alert(e.message || i18next.t('errors:generic')); }
 finally { setSaving(false); }
 };

 return (
 <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' as const }}>
 <input value={name} onInput={(e: any) => setName(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 12, flex: 1, minWidth: 120 }} />
 <input type="number" value={budget} onInput={(e: any) => setBudget(Number(e.target.value))} min={0} style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 12, width: 80 }} />
 <Button variant="primary" size="xs" onClick={handleSave} disabled={saving}>{saving ? '...' : ''}</Button>
 <Button variant="ghost" size="xs" onClick={onDone}></Button>
 </div>
 );
}

function CustomerTokenCard({ token }: { token: CustomerRouterToken }) {
 const [revoking, setRevoking] = useState(false);
 const [renewing, setRenewing] = useState(false);
 const [showTokenInfo, setShowTokenInfo] = useState(false);
 const status = token.status || 'active';
 const isEditing = editingTokenId.value === token.id;
 const budgetPct = token.budget_limit_monthly > 0 ? Math.min(100, Math.round((token.budget_used_monthly / token.budget_limit_monthly) * 100)) : 0;
 const isSystem = !!(token.is_system_token);

 const handleRevoke = async () => {
 if (!confirm(` Revogar token "${token.name}"?\n\nEle será desativado permanentemente e não poderá mais ser usado para chamadas LLM.`)) return;
 setRevoking(true);
 try {
 await api.delete(`/router/tokens/${token.id}`);
 await fetchCustomerTokens();
 } catch (e: any) { alert(e.message || i18next.t('errors:generic')); }
 finally { setRevoking(false); }
 };

 const handleRenew = async () => {
 if (!confirm(` Renovar token "${token.name}"?\n\nO token atual será revogado e um NOVO será gerado com as mesmas configurações.\n\n Você precisará atualizar o token em todos os lugares onde ele é usado.`)) return;
 setRenewing(true);
 try {
 // Revoke old
 await api.delete(`/router/tokens/${token.id}`);
 // Create new with same settings
 const res = await api.post<{ token: CustomerRouterToken; plaintext_token: string }>('/router/tokens', {
 name: token.name,
 budget_limit_monthly: token.budget_limit_monthly,
 rate_limit_rpm: token.rate_limit_rpm,
 machine_id: token.machine_id,
 scopes: token.scopes,
 });
 newTokenPlaintext.value = res.plaintext_token;
 await fetchCustomerTokens();
 } catch (e: any) { alert(e.message || i18next.t('common:profile.token_renew_error')); }
 finally { setRenewing(false); }
 };

 const borderColor = status === 'active' ? 'rgba(139,92,246,0.2)' : status === 'revoked' ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)';

 return (
 <div style={{
 ...cardStyle,
 background: status === 'active'
 ? 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(47,107,255,0.06))'
 : 'rgba(255,255,255,0.02)',
 border: `1px solid ${borderColor}`,
 opacity: status === 'revoked' ? 0.6 : 1,
 }}>
 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <h3 style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: 0 }}>{token.name}</h3>
 <TokenStatusBadge status={status} />
 </div>
 <div style={{ display: 'flex', gap: 6 }}>
 {isSystem && (
 <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6, background: 'rgba(139,92,246,0.12)', color: T.purple, fontWeight: 700, letterSpacing: '0.5px' }}>SYSTEM</span>
 )}
 {status === 'active' && !isSystem && (
 <>
 <Button variant="ghost" onClick={() => editingTokenId.value = isEditing ? null : token.id}
 title="Editar nome e budget"
 style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 6, padding: '4px 10px', color: T.textSecondary, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
 Editar
 </Button>
 <Button variant="ghost" onClick={() => setShowTokenInfo(!showTokenInfo)}
 title="Revelar / info do token"
 style={{ background: 'rgba(47,107,255,0.1)', border: '1px solid rgba(47,107,255,0.3)', borderRadius: 6, padding: '4px 10px', color: T.primary, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
 Info
 </Button>
 <Button variant="ghost" onClick={handleRenew} disabled={renewing}
 title="Renovar — revogar atual e gerar novo token"
 style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, padding: '4px 10px', color: T.warning, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
 {renewing ? i18next.t('common:profile.renewing') : i18next.t('common:profile.renew')}
 </Button>
 <Button variant="ghost" onClick={handleRevoke} disabled={revoking}
 title="Revogar — desativar token permanentemente"
 style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '4px 10px', color: T.danger, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
 {revoking ? i18next.t('common:profile.revoking') : i18next.t('common:profile.revoke')}
 </Button>
 </>
 )}
 </div>
 </div>

 {/* Token prefix (read-only, no copy) — hidden for system tokens */}
 {!isSystem && (
 <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: '5px 10px', fontFamily: 'monospace', fontSize: 11, color: T.textMuted, marginBottom: 8 }}>
   {token.token_prefix}<span style={{ color: T.textMuted }}>••••••••</span>
 </div>
 )}

 {/* Budget bar */}
 {token.budget_limit_monthly > 0 && budgetPct > 0 && (
 <div style={{ marginBottom: 8 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.textSecondary, marginBottom: 4 }}>
 <span> Budget: ${token.budget_used_monthly.toFixed(2)} / ${token.budget_limit_monthly.toFixed(2)}</span>
 <span>{budgetPct}%</span>
 </div>
 <div style={{ height: 6, borderRadius: 3, background: T.border, overflow: 'hidden' }}>
 <div style={{
 height: '100%', borderRadius: 3,
 width: `${budgetPct}%`,
 background: budgetPct >= 90 ? T.danger : budgetPct >= 70 ? T.warning : T.success,
 transition: 'width 0.3s',
 }} />
 </div>
 </div>
 )}

 {/* Meta */}
 <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' as const, fontSize: 12, color: T.textSecondary }}>
 <span> {token.rate_limit_rpm} RPM</span>
 <span> {(token.total_requests || 0).toLocaleString()} req</span>
 {token.total_cost ? <span> ${token.total_cost.toFixed(4)}</span> : null}
 <span> {formatDateShort(token.created_at)}</span>
 {token.last_used_at && <span> {formatDateShort(token.last_used_at)}</span>}
 {token.machine_id && <span> {token.machine_id.substring(0, 8)}</span>}
 </div>

 {/* Inline edit — not for system tokens */}
 {isEditing && !isSystem && <EditTokenInline token={token} onDone={() => editingTokenId.value = null} />}
 </div>
 );
}

function RateLimitsSection() {
 const [limits, setLimits] = useState<Record<string, number> | null>(null);
 const [limitsLoading, setLimitsLoading] = useState(true);
 const [limitsSaving, setLimitsSaving] = useState(false);
 const [limitsMsg, setLimitsMsg] = useState('');
 const [form, setForm] = useState({ rpm_limit: 0, rph_limit: 0, rpd_limit: 0, spend_limit_per_minute: 0, spend_limit_per_hour: 0, budget_limit_monthly: 0 });

 useEffect(() => {
  api.get<Record<string, number>>('/router/my-limits').then(data => {
   setLimits(data);
   setForm({
    rpm_limit: data.rpm_limit ?? 60,
    rph_limit: data.rph_limit ?? 600,
    rpd_limit: data.rpd_limit ?? 10000,
    spend_limit_per_minute: data.spend_limit_per_minute ?? 0,
    spend_limit_per_hour: data.spend_limit_per_hour ?? 0,
    budget_limit_monthly: data.budget_limit_monthly ?? 0,
   });
   setLimitsLoading(false);
  }).catch(() => setLimitsLoading(false));
 }, []);

 const saveLimits = async () => {
  setLimitsSaving(true);
  setLimitsMsg('');
  try {
   await api.patch('/router/my-limits', form);
   setLimitsMsg(i18next.t('common:profile.limits_updated'));
   setTimeout(() => setLimitsMsg(''), 3000);
  } catch (e: any) {
   setLimitsMsg(e.message || i18next.t('common:profile.save_error'));
  }
  setLimitsSaving(false);
 };

 if (limitsLoading) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);

 const rateFields = [
  { key: 'rpm_limit', label: i18next.t('common:profile.rpm'), unit: 'RPM', min: 1, max: 1000 },
  { key: 'rph_limit', label: i18next.t('common:profile.rph'), unit: 'RPH', min: 1, max: 10000 },
  { key: 'rpd_limit', label: i18next.t('common:profile.rpd'), unit: 'RPD', min: 1, max: 100000 },
 ];
 const spendFields = [
  { key: 'spend_limit_per_minute', label: i18next.t('common:profile.spend_per_min'), unit: 'USD', min: 0, max: 1000, prefix: '$' },
  { key: 'spend_limit_per_hour', label: i18next.t('common:profile.spend_per_hour'), unit: 'USD', min: 0, max: 10000, prefix: '$' },
  { key: 'budget_limit_monthly', label: i18next.t('common:profile.spend_per_month'), unit: 'USD', min: 0, max: 100000, prefix: '$' },
 ];

 const LimitField = ({ f }: { f: { key: string; label: string; unit: string; min: number; max: number; prefix?: string } }) => (
  <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 10, padding: '12px 14px', border: '1px solid rgba(255,255,255,0.06)' }}>
   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
    <label style={{ fontSize: 12, color: T.textSecondary, fontWeight: 600 }}>{f.label}</label>
    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(139,92,246,0.1)', color: T.purple, fontWeight: 600 }}>{f.unit}</span>
   </div>
   <div style={{ position: 'relative' }}>
    {f.prefix && <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: T.textMuted, fontWeight: 600 }}>{f.prefix}</span>}
    <input
     type="number" min={f.min} max={f.max}
     value={form[f.key as keyof typeof form]}
     onInput={(e) => setForm({ ...form, [f.key]: Number((e.target as HTMLInputElement).value) })}
     style={{ ...inputStyle, width: '100%', paddingLeft: f.prefix ? 28 : 12, boxSizing: 'border-box' as const }}
    />
   </div>
   <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
    <span style={{ fontSize: 10, color: T.textMuted }}>Min: {f.min}</span>
    <span style={{ fontSize: 10, color: T.textMuted }}>Max: {f.max.toLocaleString()}</span>
   </div>
  </div>
 );

 return (
  <div style={{ ...cardStyle, marginTop: 20 }}>
   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
    <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: 0 }}>Configuracoes Avancadas</h3>
    {form.rpm_limit > 0 && <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: 'rgba(16,185,129,0.1)', color: T.success, fontWeight: 600 }}>Ativo</span>}
   </div>
   <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 20 }}>Defina limites de uso para proteger contra gastos inesperados. Valores 0 = sem limite.</p>

   {/* Rate Limits */}
   <div style={{ fontSize: 12, fontWeight: 700, color: T.purple, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>Limites de Requisicoes</div>
   <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
    {rateFields.map(f => <LimitField key={f.key} f={f} />)}
   </div>

   {/* Spend Limits */}
   <div style={{ fontSize: 12, fontWeight: 700, color: T.warning, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>Limites de Gasto</div>
   <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
    {spendFields.map(f => <LimitField key={f.key} f={f} />)}
   </div>

   <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <Button variant="ghost" onClick={saveLimits} disabled={limitsSaving} style={{
     padding: '10px 24px', borderRadius: 10, border: 'none',
     background: limitsSaving ? T.border : 'linear-gradient(135deg, #2F6BFF 0%, #6D4CFF 100%)',
     color: T.text, fontSize: 13, fontWeight: 700, cursor: limitsSaving ? 'not-allowed' : 'pointer',
    }}>
     {limitsSaving ? i18next.t('common:processing') : i18next.t('common:profile.save_limits')}
    </Button>
    {limitsMsg && <span style={{ fontSize: 12, color: limitsMsg.includes('sucesso') ? T.success : T.danger }}>{limitsMsg}</span>}
   </div>
  </div>
 );
}

interface PricingModel {
  provider: string; model: string; available: boolean;
  full_price_input_per_1m: number; full_price_output_per_1m: number;
  your_price_input_per_1m: number; your_price_output_per_1m: number;
  savings_input_per_1m: number; savings_output_per_1m: number;
}
interface PricingData {
  plan: string; discount_pct: number; discount_source: string; models: PricingModel[];
}

function TabRouterLLM() {
  const { t } = useTranslation('common');
 const [pricingData, setPricingData] = useState<PricingData | null>(null);
 const [pricingLoading, setPricingLoading] = useState(true);

 useEffect(() => {
   fetchUsage();
   fetchModelUsage();
   api.get<PricingData>('/router/pricing').then(d => { setPricingData(d); setPricingLoading(false); }).catch(() => setPricingLoading(false));
 }, []);

 if (usageLoading.value) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);
 const d = usageData.value;

 return (
 <>
 {/* 1. Consumo Hoje */}
 {d && (
 <div style={{ marginBottom: 20 }}>
 <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Consumo Hoje</h3>
 <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' as const }}>
 <StatCard emoji="" label="Tokens" value={fmtTokens(d.today.tokens)} sub={`≈ ${fmtUsd(d.today.tokens / 500)} valor`} />
 <StatCard emoji="" label="Chamadas" value={String(d.today.calls)} sub={`≈ ${fmtUsd(d.today.calls / 100)} valor`} />
 <StatCard emoji="" label="Custo USD" value={fmtUsd(d.today.cost_usd)} />
 </div>
 </div>
 )}

 {/* 2. Consumo do Mês */}
 {d && (
 <div style={{ marginBottom: 20 }}>
 <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Consumo do Mês</h3>
 <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' as const }}>
 <StatCard emoji="" label="Tokens" value={fmtTokens(d.month.tokens)} sub={`≈ ${fmtUsd(d.month.tokens / 500)} valor`} />
 <StatCard emoji="" label="Chamadas" value={String(d.month.calls)} sub={`≈ ${fmtUsd(d.month.calls / 100)} valor`} />
 <StatCard emoji="" label="Custo USD" value={fmtUsd(d.month.cost_usd)} />
 </div>
 </div>
 )}

 {/* 3. Status do Limite */}
 {d && (
 <div style={{ ...cardStyle, marginBottom: 20 }}>
 <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 8 }}>Status do Limite</h3>
 <LimitBar used={d.month.tokens} total={MONTHLY_TOKEN_LIMIT} />
 <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 8 }}>
 Conversão: $1 = 500 tokens · $1 = 100 chamadas
 </div>
 </div>
 )}

 {/* 4. Últimos 30 dias */}
 {d && (
 <div style={{ ...cardStyle, marginBottom: 20 }}>
 <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Últimos 30 dias (tokens)</h3>
 <UsageChart daily={d.daily} />
 </div>
 )}

 {/* 5. Tabela de Preços por Modelo (sempre aberta) */}
 <div style={{ ...cardStyle, marginBottom: 20 }}>
   <div style={{ marginBottom: 12 }}>
     <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Tabela de Preços por Modelo</div>
     <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
       Plano: <span style={{ color: T.primary, fontWeight: 600 }}>{pricingData?.plan || '...'}</span>
       {pricingData && pricingData.discount_pct > 0 && (
         <span style={{ marginLeft: 8, color: T.success, fontWeight: 600 }}>
           {pricingData.discount_pct}% de desconto {pricingData.discount_source === 'custom' ? '(personalizado)' : '(do plano)'}
         </span>
       )}
     </div>
   </div>

   {!pricingLoading && pricingData && (
     <div style={{ overflowX: 'auto' as const }}>
       {pricingData.discount_pct > 0 && (
         <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: T.success }}>
           Você tem <strong>{pricingData.discount_pct}%</strong> de desconto em todos os modelos! Os valores abaixo já refletem seu desconto.
         </div>
       )}
       <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
         <thead>
           <tr style={{ borderBottom: `1px solid ${T.border}` }}>
             <th style={{ padding: '8px 10px', textAlign: 'left', color: T.textMuted, fontSize: 11, textTransform: 'uppercase' as const }}>Status</th>
             <th style={{ padding: '8px 10px', textAlign: 'left', color: T.textMuted, fontSize: 11, textTransform: 'uppercase' as const }}>Provedor</th>
             <th style={{ padding: '8px 10px', textAlign: 'left', color: T.textMuted, fontSize: 11, textTransform: 'uppercase' as const }}>Modelo</th>
             <th style={{ padding: '8px 10px', textAlign: 'center', color: T.textMuted, fontSize: 11, textTransform: 'uppercase' as const }}>Tipo</th>
             {pricingData.discount_pct > 0 && (
               <>
                 <th style={{ padding: '8px 10px', textAlign: 'right', color: T.textMuted, fontSize: 11, textTransform: 'uppercase' as const }}>Preço Cheio In/1M</th>
                 <th style={{ padding: '8px 10px', textAlign: 'right', color: T.textMuted, fontSize: 11, textTransform: 'uppercase' as const }}>Preço Cheio Out/1M</th>
               </>
             )}
             <th style={{ padding: '8px 10px', textAlign: 'right', color: pricingData.discount_pct > 0 ? T.success : T.textMuted, fontSize: 11, textTransform: 'uppercase' as const }}>
               {pricingData.discount_pct > 0 ? t('common:profile.your_price_in') : 'Input/1M tokens'}
             </th>
             <th style={{ padding: '8px 10px', textAlign: 'right', color: pricingData.discount_pct > 0 ? T.success : T.textMuted, fontSize: 11, textTransform: 'uppercase' as const }}>
               {pricingData.discount_pct > 0 ? t('common:profile.your_price_out') : 'Output/1M tokens'}
             </th>
             {pricingData.discount_pct > 0 && (
               <th style={{ padding: '8px 10px', textAlign: 'right', color: T.warning, fontSize: 11, textTransform: 'uppercase' as const }}>Economia</th>
             )}
           </tr>
         </thead>
         <tbody>
           {pricingData.models.filter((m: any) => m.available !== false).map((m: any, i: number) => (
             <tr key={i} style={{ borderBottom: '1px solid #2F2F2F' }}>
               <td style={{ padding: '8px 10px' }}>
                 <span style={{
                   display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
                   borderRadius: 10, fontSize: 10, fontWeight: 700,
                   background: 'rgba(16,185,129,0.12)', color: T.success,
                 }}>
                   <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.success }} />
                   Ativo
                 </span>
               </td>
               <td style={{ padding: '8px 10px', color: T.textSecondary }}>{m.provider}</td>
               <td style={{ padding: '8px 10px' }}>
                 <div style={{ color: T.text, fontWeight: 500 }}>{m.display_name || m.model}</div>
                 <div style={{ color: T.textMuted, fontSize: 10, fontFamily: 'monospace' }}>{m.model}</div>
               </td>
               <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                 {(() => {
                   const mod = m.modality || 'text';
                   const cfg: Record<string, { label: string; bg: string; color: string }> = {
                     text: { label: i18next.t('common:profile.text'), bg: 'rgba(59,130,246,0.12)', color: T.primary },
                     image: { label: i18next.t('common:profile.image_type'), bg: 'rgba(168,85,247,0.12)', color: T.purple },
                     audio: { label: i18next.t('common:profile.audio'), bg: 'rgba(245,158,11,0.12)', color: T.warning },
                     multimodal: { label: 'Multi', bg: 'rgba(16,185,129,0.12)', color: T.success },
                   };
                   const c = cfg[mod] || cfg.text;
                   return (
                     <span style={{
                       display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                       fontSize: 10, fontWeight: 700, background: c.bg, color: c.color,
                     }}>{c.label}</span>
                   );
                 })()}
               </td>
               {pricingData.discount_pct > 0 && (
                 <>
                   <td style={{ padding: '8px 10px', textAlign: 'right', color: T.textMuted, textDecoration: 'line-through' }}>${m.full_price_input_per_1m.toFixed(2)}</td>
                   <td style={{ padding: '8px 10px', textAlign: 'right', color: T.textMuted, textDecoration: 'line-through' }}>${m.full_price_output_per_1m.toFixed(2)}</td>
                 </>
               )}
               <td style={{ padding: '8px 10px', textAlign: 'right', color: pricingData.discount_pct > 0 ? T.success : T.text, fontWeight: 600 }}>${m.your_price_input_per_1m.toFixed(2)}</td>
               <td style={{ padding: '8px 10px', textAlign: 'right', color: pricingData.discount_pct > 0 ? T.success : T.text, fontWeight: 600 }}>${m.your_price_output_per_1m.toFixed(2)}</td>
               {pricingData.discount_pct > 0 && (
                 <td style={{ padding: '8px 10px', textAlign: 'right', color: T.warning, fontWeight: 600 }}>
                   -${(m.savings_input_per_1m + m.savings_output_per_1m).toFixed(2)}
                 </td>
               )}
             </tr>
           ))}
         </tbody>
       </table>

       {/* Unavailable models — collapsible */}
       {(() => {
         const unavailable = pricingData.models.filter((m: any) => m.available === false);
         if (unavailable.length === 0) return null;
         return (
           <details style={{ marginTop: 16 }}>
             <summary style={{
               cursor: 'pointer', fontSize: 13, color: T.textMuted, fontWeight: 600,
               padding: '10px 14px', borderRadius: 8,
               background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.1)',
               listStyle: 'none', display: 'flex', alignItems: 'center', gap: 8,
               userSelect: 'none',
             }}>
               <span style={{ fontSize: 12, transition: 'transform 0.2s' }}></span>
               <span>Modelos indisponíveis ({unavailable.length})</span>
               <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 400, marginLeft: 4 }}>— clique para expandir</span>
             </summary>
             <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
               <tbody>
                 {unavailable.map((m: any, i: number) => (
                   <tr key={i} style={{ borderBottom: '1px solid #2F2F2F', opacity: 0.5 }}>
                     <td style={{ padding: '8px 10px' }}>
                       <span style={{
                         display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
                         borderRadius: 10, fontSize: 10, fontWeight: 700,
                         background: 'rgba(239,68,68,0.12)', color: T.danger,
                       }}>
                         <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.danger }} />
                         Indisponível
                       </span>
                     </td>
                     <td style={{ padding: '8px 10px', color: T.textSecondary }}>{m.provider}</td>
                     <td style={{ padding: '8px 10px' }}>
                       <div style={{ color: T.text, fontWeight: 500 }}>{m.display_name || m.model}</div>
                       <div style={{ color: T.textMuted, fontSize: 10, fontFamily: 'monospace' }}>{m.model}</div>
                     </td>
                     <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                       {(() => {
                         const mod = m.modality || 'text';
                         const cfg: Record<string, { label: string; bg: string; color: string }> = {
                           text: { label: i18next.t('common:profile.text'), bg: 'rgba(59,130,246,0.12)', color: T.primary },
                           image: { label: i18next.t('common:profile.image_type'), bg: 'rgba(168,85,247,0.12)', color: T.purple },
                           audio: { label: i18next.t('common:profile.audio'), bg: 'rgba(245,158,11,0.12)', color: T.warning },
                           multimodal: { label: 'Multi', bg: 'rgba(16,185,129,0.12)', color: T.success },
                         };
                         const c = cfg[mod] || cfg.text;
                         return (
                           <span style={{
                             display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                             fontSize: 10, fontWeight: 700, background: c.bg, color: c.color,
                           }}>{c.label}</span>
                         );
                       })()}
                     </td>
                     {pricingData.discount_pct > 0 && (
                       <>
                         <td style={{ padding: '8px 10px', textAlign: 'right', color: T.textMuted, textDecoration: 'line-through' }}>${m.full_price_input_per_1m.toFixed(2)}</td>
                         <td style={{ padding: '8px 10px', textAlign: 'right', color: T.textMuted, textDecoration: 'line-through' }}>${m.full_price_output_per_1m.toFixed(2)}</td>
                       </>
                     )}
                     <td style={{ padding: '8px 10px', textAlign: 'right', color: T.textMuted, fontWeight: 600 }}>${m.your_price_input_per_1m.toFixed(2)}</td>
                     <td style={{ padding: '8px 10px', textAlign: 'right', color: T.textMuted, fontWeight: 600 }}>${m.your_price_output_per_1m.toFixed(2)}</td>
                     {pricingData.discount_pct > 0 && (
                       <td style={{ padding: '8px 10px', textAlign: 'right', color: T.warning, fontWeight: 600 }}>
                         -${(m.savings_input_per_1m + m.savings_output_per_1m).toFixed(2)}
                       </td>
                     )}
                   </tr>
                 ))}
               </tbody>
             </table>
           </details>
         );
       })()}

       <div style={{ fontSize: 11, color: T.textMuted, marginTop: 10 }}>
         Preços por 1 milhão de tokens. Valores iguais aos cobrados pelos provedores oficiais.
         {pricingData.discount_pct > 0 && ` Seu desconto de ${pricingData.discount_pct}% é aplicado automaticamente em cada request.`}
       </div>

       {/* Upgrade CTA */}
       {pricingData.discount_pct < 15 && (
         <div style={{
           marginTop: 16, padding: '16px 20px', borderRadius: 12,
           background: 'linear-gradient(135deg, rgba(47,107,255,0.08) 0%, rgba(124,58,237,0.08) 100%)',
           border: '1px solid rgba(47,107,255,0.2)',
           display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: 12,
         }}>
           <div>
             <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
               {pricingData.discount_pct === 0 ? t('common:profile.save_15') : t('common:profile.want_more_discount')}
             </div>
             <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>
               {pricingData.discount_pct === 0
                 ? t('common:profile.paid_plans_discount')
                 : `Você tem ${pricingData.discount_pct}% agora. Planos superiores oferecem até 15% de desconto em todos os tokens.`
               }
             </div>
           </div>
           <Button variant="ghost" onClick={() => { window.location.href = '/upgrade'; }} style={{
             padding: '10px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
             background: T.primary, color: T.text, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' as const,
           }}>Ver Planos</Button>
         </div>
       )}
     </div>
   )}
   {pricingLoading && (
     <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
   )}
 </div>

 {/* 6. Modelos Usados por Mês */}
 {d && d.usage && (d.usage.models_used.length > 0 || d.usage.last_used_at) && (
 <div style={{ ...cardStyle, marginBottom: 20 }}>
 <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Modelos Usados (Mês)</h3>
 <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, marginBottom: 12 }}>
 {d.usage.models_used.length > 0 ? d.usage.models_used.map((m: string) => (
 <span key={m} style={{
 fontSize: 11, padding: '4px 10px', borderRadius: 20,
 background: 'rgba(139,92,246,0.12)', color: T.purple, fontWeight: 500,
 }}>{m}</span>
 )) : (
 <span style={{ color: T.textSecondary, fontSize: 12 }}>Nenhum modelo usado este mês</span>
 )}
 </div>
 {d.usage.last_used_at && (
 <div style={{ fontSize: 12, color: T.textSecondary }}>
 Último uso: {formatDate(d.usage.last_used_at)}
 </div>
 )}
 {d.usage.requests_month > 0 && (
 <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: T.textSecondary }}>
 <span>{d.usage.requests_month} requests</span>
 <span>{fmtTokens(d.usage.tokens_in)} in</span>
 <span>{fmtTokens(d.usage.tokens_out)} out</span>
 </div>
 )}
 </div>
 )}

 {/* Gasto por Modelo */}
 {modelUsageData.value.length > 0 && (() => {
  const models = modelUsageData.value;
  const totalCost = models.reduce((s: number, m: any) => s + m.cost_usd, 0);
  const providerColors: Record<string, string> = { anthropic: T.warningHover, openai: T.success, google: T.primary };
  return (
   <div style={{ background: T.bg, borderRadius: 12, padding: 20, border: '1px solid rgba(255,255,255,0.06)', marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 14 }}>
     <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="4" y="4" width="16" height="16" rx="3" stroke="#7c5cfc" strokeWidth="1.8" fill="none"/><circle cx="9" cy="10" r="1.5" fill="#7c5cfc"/><circle cx="15" cy="10" r="1.5" fill="#7c5cfc"/><path d="M8 15.5c0-1 1.8-2 4-2s4 1 4 2" stroke="#7c5cfc" strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg>
     Gasto por Modelo
    </div>
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
     {models.map((m: any, i: number) => {
      const pct = totalCost > 0 ? (m.cost_usd / totalCost) * 100 : 0;
      const color = providerColors[m.provider?.toLowerCase()] || T.purple;
      return (
       <div key={i} style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.02)', transition: 'background 0.15s', cursor: 'default' }}
        onMouseEnter={(e: any) => { e.currentTarget.style.background = T.bgCardHover; }}
        onMouseLeave={(e: any) => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
         <span style={{ color: T.text, fontWeight: 500 }}>{m.model}</span>
         <span style={{ color: T.textSecondary }}>${m.cost_usd.toFixed(4)} · {pct.toFixed(1)}%</span>
        </div>
        <div style={{ width: '100%', height: 6, borderRadius: 3, background: T.border }}>
         <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: color, transition: 'width 0.3s' }} />
        </div>
       </div>
      );
     })}
    </div>
    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 10 }}>Total do mês: ${totalCost.toFixed(4)} · {models.reduce((s: number, m: any) => s + m.calls, 0)} chamadas</div>
   </div>
  );
 })()}

 {/* 7. Limite de Contexto */}
 <ContextLimitSection />

 {/* 7b. Limite de Output */}
 <OutputLimitSection />

 {/* 8. Configurações Avançadas */}
 <RateLimitsSection />
 </>
 );
}

// ── Context Limit Section ──
function ContextLimitSection() {
  const { t } = useTranslation('common');
  const [maxTokens, setMaxTokens] = useState(0);
  const [action, setAction] = useState<'none' | 'truncate' | 'block'>('none');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get<{ max_context_tokens: number; context_limit_action: string }>('/router/context-settings')
      .then(d => {
        setMaxTokens(d.max_context_tokens || 0);
        setAction((d.context_limit_action as any) || 'none');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put('/router/context-settings', { max_context_tokens: maxTokens, context_limit_action: action });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) {
      console.error('Failed to save context settings:', e);
      setError(e?.message || i18next.t('common:profile.save_error_retry'));
    } finally {
      setSaving(false);
    }
  };

  const [error, setError] = useState('');
  const presets = [
    { label: '8K', value: 8000 },
    { label: '16K', value: 16000 },
    { label: '32K', value: 32000 },
    { label: '64K', value: 64000 },
    { label: '128K', value: 128000 },
    { label: '200K', value: 200000 },
    { label: i18next.t('common:profile.no_limit'), value: 0 },
  ];

  if (loading) return <SkeletonBar width="100%" height={80} style={{ marginBottom: 20 }} />;

  return (
    <div style={{ ...cardStyle, marginBottom: 20 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>Limite de Contexto</h3>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16, lineHeight: 1.5 }}>
        Controle o tamanho máximo de tokens enviados por request. Útil para evitar cobranças altas quando o histórico de conversa cresce demais.
      </p>

      {/* Presets */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>Máximo de tokens por request</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
          {presets.map(p => (
            <Button
              key={p.value}
              size="sm"
              variant={maxTokens === p.value ? 'primary' : 'ghost'}
              onClick={() => setMaxTokens(p.value)}
              style={{ fontSize: 12 }}
            >
              {p.label}
            </Button>
          ))}
        </div>
        <div style={{ marginTop: 8 }}>
          <UiInput
            label="Ou digite um valor customizado"
            type="number"
            value={maxTokens === 0 ? '' : String(maxTokens)}
            onChange={(e: any) => setMaxTokens(parseInt(e.target.value) || 0)}
            placeholder="0 = sem limite"
            style={{ maxWidth: 250 }}
          />
        </div>
        {maxTokens > 0 && (
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
            ≈ {Math.round(maxTokens * 4 / 1024)}KB de texto · ≈ ${((maxTokens / 1_000_000) * 3).toFixed(4)} por request (Haiku) · ≈ ${((maxTokens / 1_000_000) * 15).toFixed(4)} por request (Opus)
          </div>
        )}
      </div>

      {/* Action when exceeded */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>Quando o limite for excedido</div>
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
          {([
            { value: 'truncate', label: i18next.t('common:profile.truncate'), desc: i18next.t('common:profile.truncate_desc') },
            { value: 'block', label: i18next.t('common:profile.block_request'), desc: i18next.t('common:profile.block_desc') },
            { value: 'none', label: i18next.t('common:profile.no_limit_default'), desc: i18next.t('common:profile.no_limit_desc') },
          ] as const).map(opt => (
            <div
              key={opt.value}
              onClick={() => setAction(opt.value)}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                border: `1px solid ${action === opt.value ? T.primary : T.border}`,
                background: action === opt.value ? 'rgba(109,76,255,0.08)' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: action === opt.value ? T.primary : T.text }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>{opt.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button onClick={save} disabled={saving} size="sm">
          {saving ? t('common:processing') : saved ? t('common:profile.saved') : t('common:profile.save_config')}
        </Button>
        {error && <span style={{ color: '#ef4444', fontSize: 12 }}>❌ {error}</span>}
      </div>
    </div>
  );
}

// ── Output Limit Section ──
function OutputLimitSection() {
  const { t } = useTranslation('common');
  const [maxOutput, setMaxOutput] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get<{ max_output_tokens: number }>('/router/output-settings')
      .then(d => setMaxOutput(d.max_output_tokens || 0))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put('/router/output-settings', { max_output_tokens: maxOutput });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) {
      console.error('Failed to save output settings:', e);
      setError(e?.message || i18next.t('common:profile.save_error_retry'));
    } finally {
      setSaving(false);
    }
  };

  const presets = [
    { label: '4K', value: 4096 },
    { label: '8K', value: 8192 },
    { label: '16K', value: 16384 },
    { label: '32K', value: 32000 },
    { label: i18next.t('common:profile.system_default'), value: 0 },
  ];

  if (loading) return <SkeletonBar width="100%" height={80} style={{ marginBottom: 20 }} />;

  return (
    <div style={{ ...cardStyle, marginBottom: 20 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>Limite de Output (Resposta)</h3>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16, lineHeight: 1.5 }}>
        Controle o tamanho máximo de tokens que o modelo pode gerar por resposta. Útil para limitar respostas muito longas e controlar custos de output.
      </p>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>Máximo de tokens por resposta</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
          {presets.map(p => (
            <Button
              key={p.value}
              size="sm"
              variant={maxOutput === p.value ? 'primary' : 'ghost'}
              onClick={() => setMaxOutput(p.value)}
              style={{ fontSize: 12 }}
            >
              {p.label}
            </Button>
          ))}
        </div>
        <div style={{ marginTop: 8 }}>
          <UiInput
            label="Ou digite um valor customizado"
            type="number"
            value={maxOutput === 0 ? '' : String(maxOutput)}
            onChange={(e: any) => setMaxOutput(parseInt(e.target.value) || 0)}
            placeholder="0 = usar padrão do sistema"
            style={{ maxWidth: 250 }}
          />
        </div>
        {maxOutput > 0 && (
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
            ≈ {Math.round(maxOutput * 4 / 1024)}KB de texto por resposta · ≈ ${((maxOutput / 1_000_000) * 15).toFixed(4)} custo máx/resposta (Haiku) · ≈ ${((maxOutput / 1_000_000) * 75).toFixed(4)} custo máx/resposta (Opus)
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button onClick={save} disabled={saving} size="sm">
          {saving ? t('common:processing') : saved ? t('common:profile.saved') : t('common:profile.save_config')}
        </Button>
        {error && <span style={{ color: '#ef4444', fontSize: 12 }}>❌ {error}</span>}
      </div>
    </div>
  );
}

// Tab: Proof 

function TabProof() {
  const { t } = useTranslation('common');
 if (proofLoading.value && !proofData.value) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);
 const d = proofData.value;

 return (
 <>
 {/* Period selector */}
 <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' as const, alignItems: 'center' }}>
 {(['month', 'quarter', 'year'] as const).map(p => (
 <Button variant="ghost" key={p} onClick={() => { proofPeriod.value = p; fetchProof(1); }} style={{
 padding: '6px 14px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600,
 background: proofPeriod.value === p ? T.primary : T.border,
 color: proofPeriod.value === p ? '#fff' : T.textSecondary, cursor: 'pointer', fontFamily: 'inherit',
 }}>
 {p === 'month' ? t('common:profile.month') : p === 'quarter' ? t('common:profile.quarter') : t('common:profile.year')}
 </Button>
 ))}

 {/* Filters */}
 <select value={proofFilterType.value} onChange={(e) => { proofFilterType.value = (e.target as HTMLSelectElement).value; fetchProof(1); }}
 style={{ ...inputStyle, width: 'auto', padding: '6px 10px', fontSize: 12 }}>
 <option value="">Todos os tipos</option>
 <option value="prompt">Prompt</option>
 <option value="completion">Completion</option>
 <option value="api">API</option>
 </select>

 <input type="text" placeholder="Filtrar projeto..." value={proofFilterProject.value}
 onInput={(e) => { proofFilterProject.value = (e.target as HTMLInputElement).value; }}
 onKeyDown={(e) => { if ((e as KeyboardEvent).key === 'Enter') fetchProof(1); }}
 style={{ ...inputStyle, width: 160, padding: '6px 10px', fontSize: 12 }} />

 <Button variant="ghost" onClick={() => fetchProof(1)} style={{
 padding: '6px 12px', borderRadius: 8, border: 'none', background: T.border,
 color: T.textSecondary, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
 }}></Button>
 </div>

 {/* Summary cards */}
 {d?.summary && (
 <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' as const, marginBottom: 20 }}>
 <StatCard emoji="" label="Total Tokens" value={fmtTokens(d.summary.tokens)} />
 <StatCard emoji="" label="Total Chamadas" value={String(d.summary.calls)} />
 <StatCard emoji="" label="Total Custo" value={fmtUsd(d.summary.cost_usd)} />
 <StatCard emoji="" label="Período" value={d.summary.first_date ? `${formatDateShort(d.summary.first_date)} — ${formatDateShort(d.summary.last_date || '')}` : 'N/A'} />
 </div>
 )}

 {/* Table */}
 <div style={{ ...cardStyle, padding: 0, overflow: 'auto' }}>
 <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 }}>
 <thead>
 <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
 {[t('common:profile.date'), t('common:profile.type'), 'Tokens', t('common:profile.cost_usd'), t('common:profile.project_task'), t('common:profile.model')].map(h => (
 <th key={h} style={{ padding: '12px 14px', textAlign: 'left' as const, color: T.textSecondary, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' as const }}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {(!d || d.logs.length === 0) && (
 <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center' as const, color: T.textSecondary }}>Nenhum registro encontrado</td></tr>
 )}
 {d?.logs.map(log => (
 <tr key={log.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
 <td style={{ padding: '10px 14px', color: T.text }}>{formatDate(log.created_at)}</td>
 <td style={{ padding: '10px 14px' }}>
 <span style={{
 padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
 background: log.type === 'prompt' ? 'rgba(47,107,255,0.12)' : log.type === 'completion' ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)',
 color: log.type === 'prompt' ? T.primary : log.type === 'completion' ? T.success : T.warning,
 }}>{log.type}</span>
 </td>
 <td style={{ padding: '10px 14px', color: T.text, fontFamily: 'monospace' }}>{log.tokens.toLocaleString()}</td>
 <td style={{ padding: '10px 14px', color: T.text, fontFamily: 'monospace' }}>{fmtUsd(log.cost_usd)}</td>
 <td style={{ padding: '10px 14px', color: T.textSecondary }}>{[log.project, log.task].filter(Boolean).join(' / ') || '—'}</td>
 <td style={{ padding: '10px 14px', color: T.textSecondary, fontSize: 11 }}>{log.model || '—'}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>

 {/* Pagination + Export */}
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, flexWrap: 'wrap' as const, gap: 8 }}>
 <div style={{ display: 'flex', gap: 8 }}>
 <Button variant="ghost" disabled={!d || d.page <= 1} onClick={() => fetchProof(proofPage.value - 1)} style={{
 padding: '6px 14px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600,
 background: T.border, color: T.textSecondary, cursor: !d || d.page <= 1 ? 'not-allowed' : 'pointer',
 opacity: !d || d.page <= 1 ? 0.4 : 1, fontFamily: 'inherit',
 }}>← Anterior</Button>
 <span style={{ fontSize: 12, color: T.textSecondary, lineHeight: '32px' }}>
 {d ? `${d.page} / ${d.pages || 1} (${d.total} registros)` : '—'}
 </span>
 <Button variant="ghost" disabled={!d || d.page >= d.pages} onClick={() => fetchProof(proofPage.value + 1)} style={{
 padding: '6px 14px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600,
 background: T.border, color: T.textSecondary, cursor: !d || d.page >= d.pages ? 'not-allowed' : 'pointer',
 opacity: !d || d.page >= d.pages ? 0.4 : 1, fontFamily: 'inherit',
 }}>Próxima →</Button>
 </div>
 <div style={{ display: 'flex', gap: 8 }}>
 <Button variant="ghost" onClick={downloadCsv} style={{
 padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(47,107,255,0.3)',
 background: 'rgba(47,107,255,0.08)', color: T.primary, fontSize: 12, fontWeight: 600,
 cursor: 'pointer', fontFamily: 'inherit',
 }}> Exportar CSV</Button>
 <Button variant="ghost" onClick={() => alert(t('common:profile.export_soon'))} style={{
 padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
 background: T.bgCard, color: T.textSecondary, fontSize: 12, fontWeight: 600,
 cursor: 'pointer', fontFamily: 'inherit',
 }}> PDF (em breve)</Button>
 </div>
 </div>
 </>
 );
}

// Tab: Configurações 

function TabConfig() {
  const { t } = useTranslation('common');
 const n = notifications.value;
 const allOff = n.all_disabled;

 return (
 <div style={cardStyle}>
 <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
 <span></span> Preferências de Notificação
 </h2>
 <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16 }}>
 Controle quais notificações você deseja receber. Desative todas de uma vez ou configure individualmente.
 </p>

 {/* Master toggle */}
 <div style={{
 display: 'flex', alignItems: 'center', justifyContent: 'space-between',
 padding: '14px 16px', borderRadius: 10, marginBottom: 16,
 background: allOff ? 'rgba(239,68,68,0.08)' : 'rgba(47,107,255,0.08)',
 border: `1px solid ${allOff ? 'rgba(239,68,68,0.2)' : 'rgba(47,107,255,0.2)'}`,
 }}>
 <div>
 <div style={{ fontSize: 14, fontWeight: 700, color: allOff ? T.danger : T.primary }}>
 {allOff ? ' Todas as notificações DESATIVADAS' : ' Notificações ativadas'}
 </div>
 <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>
 {allOff ? t('common:profile.enable_notif') : t('common:profile.disable_notif')}
 </div>
 </div>
 <ToggleSwitch enabled={!allOff} onToggle={() => updateNotification('all_disabled', !allOff)} />
 </div>

 <NotifRow label="Push (navegador)" desc="Notificações push no navegador e dispositivos móveis" enabled={n.push_enabled && !allOff} onToggle={() => updateNotification('push_enabled', !n.push_enabled)} disabled={allOff} />
 <NotifRow label="Email" desc="Notificações por email sobre tarefas e atualizações" enabled={n.email_enabled && !allOff} onToggle={() => updateNotification('email_enabled', !n.email_enabled)} disabled={allOff} />
 <NotifRow label="Chat" desc="Notificações de novas mensagens no chat" enabled={n.chat_enabled && !allOff} onToggle={() => updateNotification('chat_enabled', !n.chat_enabled)} disabled={allOff} />
 <NotifRow label="Marketing" desc="Novidades, promoções e atualizações de produto" enabled={n.marketing_enabled && !allOff} onToggle={() => updateNotification('marketing_enabled', !n.marketing_enabled)} disabled={allOff} />
 <NotifRow label="Sistema" desc="Alertas de segurança, manutenção e status do sistema" enabled={n.system_enabled && !allOff} onToggle={() => updateNotification('system_enabled', !n.system_enabled)} disabled={allOff} />

 {saveNotifsMsg.value && (
 <div style={{ fontSize: 13, color: saveNotifsMsg.value.toLowerCase().includes('salvo') || saveNotifsMsg.value.toLowerCase().includes('success') ? T.success : T.danger, marginTop: 12 }}>
 {saveNotifsMsg.value}
 </div>
 )}
 </div>
 );
}

// Main Page 

// Tab: Carteira 
const walletBalance = signal<number>(0);
const walletTx = signal<Array<{ id: string; type: string; amount: number; balance_after: number; description: string | null; created_at: string }>>([]);
const walletLoading = signal(true);
const topupAmount = signal<number>(10);
const topupMethod = signal<'pix' | 'credit_card'>('pix');
const topupLoading = signal(false);
const topupResult = signal<{ orderId?: string; qrCode?: string; qrCodeText?: string; status?: string } | null>(null);
const topupError = signal('');
const topupPolling = signal(false);
const cardNumber = signal('');
const cardName = signal('');
const cardExpMonth = signal('');
const cardExpYear = signal('');
const cardCvv = signal('');
const saveCard = signal(true);
const savedCards = signal<Array<{ id: string; name: string; last4: string; brand: string; exp_month: string; exp_year: string }>>([]);
const selectedSavedCard = signal<string | null>(null);
const usdToBrl = signal<number>(5.80);
const autoRecharge = signal<{ enabled: boolean; threshold_usd: number; recharge_amount_usd: number; discount_pct: number; monthly_limit_usd: number; card_id: string | null }>({ enabled: false, threshold_usd: 5, recharge_amount_usd: 25, discount_pct: 10, monthly_limit_usd: 100, card_id: null });
const autoRechargeSaving = signal(false);
const autoRechargeMsg = signal('');
const autoRechargeEditing = signal(false);
const showAllTx = signal(false);

async function fetchWalletData() {
 walletLoading.value = true;
 try {
 const [w, tx] = await Promise.all([
 api.get<any>('/wallet'),
 api.get<any>('/wallet/transactions?limit=30'),
 ]);
 walletBalance.value = w.wallet?.balance || 0;
 walletTx.value = tx.transactions || [];
 // Fetch USD→BRL rate + Pagar.me spread (~11%)
 try {
 const rateData = await api.get<{ rate: number }>('/subscription/exchange-rate');
 if (rateData?.rate) {
 usdToBrl.value = Math.round(rateData.rate * 1.11 * 100) / 100;
 }
 } catch {}
 // Fetch saved cards + auto-recharge config
 try {
 const [cardsRes, arRes] = await Promise.all([
 api.get<any>('/wallet/cards'),
 api.get<any>('/wallet/auto-recharge'),
 ]);
 savedCards.value = cardsRes.cards || [];
 if (arRes.config) autoRecharge.value = arRes.config;
 } catch {}
 } catch {} finally { walletLoading.value = false; }
}

// ── Signals for new wallet features ──
const subscriptionData = signal<{ current_period_end: string } | null>(null);
const modelUsageData = signal<Array<{ model: string; provider: string; cost_usd: number; tokens_in: number; tokens_out: number; calls: number }>>([]);
const spendingLimits = signal<{ soft_limit_usd: number; hard_limit_usd: number }>({ soft_limit_usd: 0, hard_limit_usd: 0 });
const spendingLimitsSaving = signal(false);
const spendingLimitsMsg = signal('');
const walletChartPeriod = signal<'7d' | '30d'>('30d');
const chartTooltip = signal<{ index: number; x: number; y: number } | null>(null);
const txFilterType = signal('all');
const txFilterPeriod = signal<'7d' | '30d' | '90d'>('30d');
const txVisibleCount = signal(20);
const topupOpen = signal(false);
const limitsOpen = signal(false);
const txHistoryOpen = signal(false);

async function fetchSubscriptionData() {
 try {
  const res = await api.get<any>('/subscription/me');
  if (res?.subscription) {
   subscriptionData.value = {
    current_period_end: res.subscription.current_period_end || '',
   };
  }
 } catch {}
}

async function fetchModelUsage() {
 try {
  const res = await api.get<{ models: typeof modelUsageData.value }>('/cloud/llm-usage/by-model');
  modelUsageData.value = res.models || [];
 } catch {}
}

async function fetchSpendingLimits() {
 try {
  const res = await api.get<{ soft_limit_usd: number; hard_limit_usd: number }>('/wallet/spending-limits');
  spendingLimits.value = { soft_limit_usd: res.soft_limit_usd || 0, hard_limit_usd: res.hard_limit_usd || 0 };
 } catch {}
}

async function saveSpendingLimits() {
 spendingLimitsSaving.value = true;
 spendingLimitsMsg.value = '';
 try {
  await api.put('/wallet/spending-limits', spendingLimits.value);
  spendingLimitsMsg.value = i18next.t('common:profile.limits_saved');
  setTimeout(() => { spendingLimitsMsg.value = ''; }, 3000);
 } catch { spendingLimitsMsg.value = i18next.t('common:profile.limits_save_error'); }
 finally { spendingLimitsSaving.value = false; }
}

function TabWallet() {
  const { t } = useTranslation('common');
 useEffect(() => {
  fetchWalletData();
  fetchUsage();
  fetchSubscriptionData();
  fetchModelUsage();
  fetchSpendingLimits();
  loadPlanData();
 }, []);

 const handleTopup = async () => {
 topupLoading.value = true;
 topupError.value = '';
 topupResult.value = null;
 try {
 const payload: any = { amount: topupAmount.value, paymentMethod: topupMethod.value };
 if (topupMethod.value === 'credit_card') {
 if (selectedSavedCard.value) {
 payload.savedCardId = selectedSavedCard.value;
 } else {
 if (!cardNumber.value || !cardName.value || !cardExpMonth.value || !cardExpYear.value || !cardCvv.value) {
 topupError.value = i18next.t('billing:fill_all_fields'); topupLoading.value = false; return;
 }
 payload.cardData = { number: cardNumber.value, holder_name: cardName.value, exp_month: cardExpMonth.value, exp_year: cardExpYear.value, cvv: cardCvv.value };
 payload.saveCard = saveCard.value;
 }
 }
 const r: any = await api.post('/wallet/topup', payload);
 topupResult.value = { orderId: r.orderId, qrCode: r.qrCode, qrCodeText: r.qrCodeText, status: r.status };
 if (r.status === 'paid') { fetchWalletData(); }
 else if (r.status === 'pending_payment' && r.orderId) {
 // Poll for PIX payment
 topupPolling.value = true;
 const poll = setInterval(async () => {
 try {
 const s: any = await api.get(`/wallet/topup/${r.orderId}/status`);
 if (s.status === 'paid') {
 clearInterval(poll);
 topupPolling.value = false;
 topupResult.value = { ...topupResult.value!, status: 'paid' };
 fetchWalletData();
 }
 } catch {}
 }, 5000);
 setTimeout(() => { clearInterval(poll); topupPolling.value = false; }, 1800000); // 30min max
 }
 } catch (e: any) {
 topupError.value = e.message || i18next.t('errors:payment_error');
 } finally { topupLoading.value = false; }
 };

 const TX_COLORS: Record<string, { label: string; color: string }> = {
 deposit: { label: i18next.t('common:profile.deposit'), color: T.success },
 earning: { label: i18next.t('common:profile.earning'), color: T.success },
 charge: { label: i18next.t('common:profile.usage'), color: T.danger },
 fee: { label: i18next.t('common:profile.fee'), color: T.warning },
 refund: { label: i18next.t('common:profile.refund'), color: T.primary },
 topup: { label: i18next.t('common:profile.topup'), color: T.success },
 topup_failed: { label: i18next.t('common:profile.declined'), color: T.danger },
 topup_pending: { label: i18next.t('common:profile.pending'), color: T.warning },
 auto_recharge: { label: i18next.t('common:profile.auto_recharge'), color: T.accent },
 admin_credit: { label: i18next.t('common:profile.credit'), color: T.success },
 llm_credit: { label: i18next.t('common:profile.llm_credit'), color: T.success },
 };

 return (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
 {/* Balance Card */}
 <div style={{
 background: 'linear-gradient(135deg, rgba(124,92,252,0.18) 0%, rgba(47,107,255,0.12) 50%, rgba(124,92,252,0.08) 100%)',
 borderRadius: 16, padding: '28px 32px', border: '1px solid rgba(124,92,252,0.25)',
 position: 'relative', overflow: 'hidden',
 }}>
 <div style={{ position: 'absolute', top: -40, right: -40, width: 120, height: 120, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,92,252,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
 <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
   <defs><linearGradient id="walletGrad" x1="0" y1="0" x2="24" y2="24"><stop offset="0%" stopColor="#7c5cfc"/><stop offset="100%" stopColor="#2F6BFF"/></linearGradient></defs>
   <rect x="2" y="6" width="20" height="14" rx="3" stroke="url(#walletGrad)" strokeWidth="1.8" fill="none"/>
   <path d="M6 6V5a3 3 0 013-3h6a3 3 0 013 3v1" stroke="url(#walletGrad)" strokeWidth="1.8" fill="none"/>
   <circle cx="17" cy="13" r="1.5" fill="url(#walletGrad)"/>
  </svg>
  <div style={{ fontSize: 13, color: T.textSecondary, fontWeight: 500, letterSpacing: 0.5 }}>Saldo disponível</div>
 </div>
 <div style={{ fontSize: 40, fontWeight: 800, color: T.text, textShadow: '0 0 30px rgba(124,92,252,0.3)', letterSpacing: -1 }}>
 $ {walletBalance.value.toFixed(2)} <span style={{ fontSize: 16, fontWeight: 400, color: T.textSecondary }}>USD</span>
 </div>
 <div style={{ fontSize: 14, color: T.textSecondary, marginTop: 4 }}>
 ≈ R$ {(walletBalance.value * usdToBrl.value).toFixed(2)} BRL
 </div>
 <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>Cotação: 1 USD = R$ {usdToBrl.value.toFixed(2)} | Usado para tarefas, orquestrador e chat com IA</div>
 </div>

 {/* MELHORIA 4: Alerta de Saldo Baixo */}


 {/* MELHORIA 2: Gráfico de Gasto Diário (CSS puro) */}
 {usageData.value && usageData.value.daily && usageData.value.daily.length > 0 && (() => {
  const allDaily = usageData.value!.daily;
  const filtered = walletChartPeriod.value === '7d' ? allDaily.slice(-7) : allDaily;
  const maxCost = Math.max(...filtered.map(d => d.cost_usd), 0.01);
  const total = filtered.reduce((s, d) => s + d.cost_usd, 0);
  const avg = filtered.length > 0 ? total / filtered.length : 0;

  return (
   <div style={{ background: T.bg, borderRadius: 12, padding: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
     <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: T.text }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><polyline points="22,6 13.5,14.5 8.5,9.5 2,16" stroke="#7c5cfc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/><polyline points="16,6 22,6 22,12" stroke="#7c5cfc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
      Gasto Diário
     </div>
     <div style={{ display: 'flex', gap: 4 }}>
      {(['7d', '30d'] as const).map(p => (
       <Button variant="ghost" key={p} onClick={() => { walletChartPeriod.value = p; }} style={{
        padding: '4px 10px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer',
        background: walletChartPeriod.value === p ? T.primary : T.border,
        color: walletChartPeriod.value === p ? '#fff' : T.textSecondary,
       }}>{p}</Button>
      ))}
     </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120, padding: '0 4px', position: 'relative' }}>
     {filtered.map((d, i) => {
      const h = Math.max(2, (d.cost_usd / maxCost) * 100);
      return (
       <div key={i}
        style={{ flex: 1, minWidth: 4, maxWidth: 20, height: `${h}%`, borderRadius: '6px 6px 2px 2px', background: 'linear-gradient(180deg, #5B8FFF, #2F6BFF)', cursor: 'pointer', transition: 'opacity 0.15s, transform 0.15s', opacity: 0.8 }}
        onMouseEnter={(e: any) => { e.target.style.opacity = '1'; e.target.style.background = 'linear-gradient(180deg, #7c5cfc, #5B8FFF)'; e.target.style.transform = 'scaleY(1.05)'; chartTooltip.value = { index: i, x: e.clientX, y: e.clientY }; }}
        onMouseLeave={(e: any) => { e.target.style.opacity = '0.8'; e.target.style.background = 'linear-gradient(180deg, #5B8FFF, #2F6BFF)'; e.target.style.transform = 'scaleY(1)'; chartTooltip.value = null; }}
       />
      );
     })}
     {chartTooltip.value && (() => {
      const d = filtered[chartTooltip.value!.index];
      if (!d) return null;
      return (
       <div style={{ position: 'fixed', left: chartTooltip.value!.x + 10, top: chartTooltip.value!.y - 40, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: T.text, zIndex: 9999, pointerEvents: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', whiteSpace: 'nowrap' }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{new Date(d.day).toLocaleDateString('pt-BR')}</div>
        <div style={{ color: T.primary }}>$ {d.cost_usd.toFixed(4)}</div>
       </div>
      );
     })()}
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 12, color: T.textSecondary }}>
     <span>Total: ${total.toFixed(4)}</span>
     <span>Média: ${avg.toFixed(4)}/dia</span>
    </div>
   </div>
  );
 })()}

 {/* Top-up Section */}
 <div id="topup-section"></div>
 <div style={{ background: T.bg, borderRadius: 12, padding: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
 {/* Auto-Recharge — only when user has saved cards */}
 {savedCards.value.length > 0 && (
 <div style={{ marginBottom: 20, padding: 20, background: autoRecharge.value.enabled ? 'rgba(124,92,252,0.04)' : T.bg, borderRadius: 12, border: `1px solid ${autoRecharge.value.enabled ? 'rgba(124,92,252,0.2)' : T.border}` }}>
 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
 <div style={{ flex: 1 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
 <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Recarga automatica</div>
 <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: '#22c55e22', color: T.success, border: '1px solid #22c55e33' }}>-10% DESCONTO</span>
 </div>
 {!autoRechargeEditing.value && autoRecharge.value.enabled ? (
 <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>{'Saldo < $'}{autoRecharge.value.threshold_usd}{' → recarga $'}{autoRecharge.value.recharge_amount_usd}{' (paga $'}{(autoRecharge.value.recharge_amount_usd * 0.9).toFixed(2)}{').'}{' Limite: $'}{autoRecharge.value.monthly_limit_usd}{'/mes.'}</div>
 ) : (
 <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>Nunca fique sem saldo — recarregue automaticamente e pague menos</div>
 )}
 </div>
 <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, marginLeft: 16 }}>
 {autoRecharge.value.enabled && !autoRechargeEditing.value && (
 <Button variant="ghost" onClick={() => { autoRechargeEditing.value = true; }} style={{ height: 28, padding: '0 12px', borderRadius: 6, border: '1px solid rgba(124,92,252,0.3)', background: 'rgba(124,92,252,0.1)', color: T.accent, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Editar</Button>
 )}
 <div onClick={() => { const ne = !autoRecharge.value.enabled; autoRecharge.value = { ...autoRecharge.value, enabled: ne }; if (ne) { autoRechargeEditing.value = true; } else { autoRechargeEditing.value = false; api.put('/wallet/auto-recharge', { ...autoRecharge.value, enabled: false }).catch(() => {}); } }}
 style={{ width: 44, height: 24, borderRadius: 12, background: autoRecharge.value.enabled ? T.accent : T.textMuted, cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
 <div style={{ width: 20, height: 20, borderRadius: 10, background: '#fff', position: 'absolute', top: 2, left: autoRecharge.value.enabled ? 22 : 2, transition: 'left 0.2s' }} />
 </div>
 </div>
 </div>

 {autoRecharge.value.enabled && autoRechargeEditing.value && (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
 <div>
 <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>Recarregar quando saldo menor que</div>
 <div style={{ display: 'flex', gap: 8 }}>
 {[1, 2, 5, 10].map(v => (
 <Button variant="ghost" key={v} onClick={() => { autoRecharge.value = { ...autoRecharge.value, threshold_usd: v }; }}
 style={{ flex: 1, height: 36, borderRadius: 8, border: `1px solid ${autoRecharge.value.threshold_usd === v ? T.accent : T.borderHover}`, background: autoRecharge.value.threshold_usd === v ? 'rgba(124,92,252,0.15)' : T.bg, color: autoRecharge.value.threshold_usd === v ? T.accent : T.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
 $ {v}
 </Button>
 ))}
 </div>
 </div>
 <div>
 <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>Valor da recarga</div>
 <div style={{ display: 'flex', gap: 8 }}>
 {[5, 10, 25, 50].map(v => (
 <Button variant="ghost" key={v} onClick={() => { autoRecharge.value = { ...autoRecharge.value, recharge_amount_usd: v }; }}
 style={{ flex: 1, height: 48, borderRadius: 8, border: `1px solid ${autoRecharge.value.recharge_amount_usd === v ? T.accent : T.borderHover}`, background: autoRecharge.value.recharge_amount_usd === v ? 'rgba(124,92,252,0.15)' : T.bg, color: autoRecharge.value.recharge_amount_usd === v ? T.accent : T.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
 <span>$ {v}</span>
 <span style={{ fontSize: 10, color: T.success }}>paga ${(v * 0.9).toFixed(2)}</span>
 </Button>
 ))}
 </div>
 </div>
 <div>
 <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>Limite mensal</div>
 <div style={{ display: 'flex', gap: 8 }}>
 {[50, 100, 200, 500].map(v => (
 <Button variant="ghost" key={v} onClick={() => { autoRecharge.value = { ...autoRecharge.value, monthly_limit_usd: v }; }}
 style={{ flex: 1, height: 36, borderRadius: 8, border: `1px solid ${autoRecharge.value.monthly_limit_usd === v ? T.accent : T.borderHover}`, background: autoRecharge.value.monthly_limit_usd === v ? 'rgba(124,92,252,0.15)' : T.bg, color: autoRecharge.value.monthly_limit_usd === v ? T.accent : T.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
 $ {v}
 </Button>
 ))}
 </div>
 </div>
 <div>
 <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>Cartao</div>
 <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
 {savedCards.value.map(card => (
 <div key={card.id} onClick={() => { autoRecharge.value = { ...autoRecharge.value, card_id: card.id }; }}
 style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: autoRecharge.value.card_id === card.id ? 'rgba(124,92,252,0.1)' : T.bg, border: `1px solid ${autoRecharge.value.card_id === card.id ? T.accent : T.borderHover}`, borderRadius: 8, cursor: 'pointer' }}>
 <span style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: 'monospace' }}>{card.brand.toUpperCase()} **** {card.last4}</span>
 <span style={{ fontSize: 11, color: T.textMuted }}>{card.exp_month}/{card.exp_year}</span>
 {autoRecharge.value.card_id === card.id && <span style={{ fontSize: 10, color: T.accent, marginLeft: 'auto' }}>Selecionado</span>}
 </div>
 ))}
 </div>
 </div>
 <div style={{ padding: 12, background: 'rgba(124,92,252,0.06)', borderRadius: 8, border: '1px solid rgba(124,92,252,0.12)' }}>
 <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.6 }}>
 Quando saldo {'<'} <strong style={{ color: T.text }}>${autoRecharge.value.threshold_usd}</strong> → recarga de <strong style={{ color: T.text }}>${autoRecharge.value.recharge_amount_usd}</strong> → voce paga <strong style={{ color: T.success }}>${(autoRecharge.value.recharge_amount_usd * 0.9).toFixed(2)}</strong> (10% off). Limite: ${autoRecharge.value.monthly_limit_usd}/mes.
 </div>
 </div>
 <Button variant="ghost" onClick={async () => {
 autoRechargeSaving.value = true; autoRechargeMsg.value = '';
 try {
 await api.put('/wallet/auto-recharge', autoRecharge.value);
 autoRechargeMsg.value = i18next.t('common:profile.saved_success');
 autoRechargeEditing.value = false;
 setTimeout(() => { autoRechargeMsg.value = ''; }, 3000);
 } catch (e: any) { autoRechargeMsg.value = e?.error?.message || e?.message || i18next.t('common:profile.save_error'); }
 finally { autoRechargeSaving.value = false; }
 }} disabled={autoRechargeSaving.value}
 style={{ width: '100%', height: 42, borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #7c5cfc, #6366f1)', color: T.text, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: autoRechargeSaving.value ? 0.6 : 1 }}>
 {autoRechargeSaving.value ? i18next.t('common:processing') : i18next.t('common:profile.save_config')}
 </Button>
 {autoRechargeMsg.value && <div style={{ fontSize: 12, color: autoRechargeMsg.value.includes('sucesso') ? T.success : T.danger, marginTop: 6, textAlign: 'center' }}>{autoRechargeMsg.value}</div>}
 </div>
 )}
 </div>
 )}

 <div onClick={() => { topupOpen.value = !topupOpen.value; }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none', marginBottom: topupOpen.value ? 16 : 0 }}>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: T.text }}>
   <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="15" rx="3" stroke="#7c5cfc" strokeWidth="1.8" fill="none"/><line x1="2" y1="10" x2="22" y2="10" stroke="#7c5cfc" strokeWidth="1.8"/><circle cx="18" cy="15" r="1.5" fill="#7c5cfc"/></svg>
   Adicionar Saldo
  </div>
  <span style={{ fontSize: 14, color: T.textSecondary, transition: 'transform 0.2s', display: 'inline-block', transform: topupOpen.value ? 'rotate(90deg)' : 'rotate(0deg)' }}>▸</span>
 </div>

 {topupOpen.value && (<div>
 {/* Amount selector */}
 <div style={{ marginBottom: 16 }}>
 <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8 }}>Valor (USD)</div>
 <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
 {[5, 10, 25, 50].map(v => (
 <Button variant="ghost" key={v} onClick={() => { topupAmount.value = v; }} style={{
 padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
 background: topupAmount.value === v && ![5,10,25,50].every(p => p !== topupAmount.value) ? T.accent : T.bgCardHover,
 color: topupAmount.value === v ? '#fff' : T.textSecondary,
 border: `1px solid ${topupAmount.value === v ? T.accent : 'rgba(255,255,255,0.08)'}`,
 transition: 'all 0.2s',
 }}>
 <div>$ {v}</div>
 </Button>
 ))}
 <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
 <span style={{ fontSize: 13, color: T.textSecondary }}>ou</span>
 <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
 <span style={{ position: 'absolute', left: 12, fontSize: 14, color: T.textSecondary, pointerEvents: 'none' }}>$</span>
 <input
 type="number"
 min="1"
 step="1"
 value={topupAmount.value}
 onInput={(e: any) => {
 const v = Math.floor(Number(e.target.value));
 if (v >= 1) topupAmount.value = v;
 }}
 onBlur={(e: any) => {
 const v = Math.floor(Number(e.target.value));
 topupAmount.value = Math.max(1, v);
 e.target.value = String(topupAmount.value);
 }}
 style={{
 width: 90, height: 40, paddingLeft: 24, paddingRight: 10,
 background: T.bg, border: '1px solid rgba(255,255,255,0.1)',
 borderRadius: 8, color: T.text, fontSize: 14, fontWeight: 600,
 outline: 'none', appearance: 'textfield', MozAppearance: 'textfield', WebkitAppearance: 'none',
 } as any}
 onFocus={(e: any) => { e.target.style.borderColor = T.accent; }}
 />
 </div>
 </div>
 </div>
 <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 8 }}>
 Você paga: <strong style={{ color: T.text }}>R$ {(topupAmount.value * usdToBrl.value).toFixed(2)}</strong> (cotação: R$ {usdToBrl.value.toFixed(2)}/USD)
 </div>
 </div>

 {/* Payment method */}
 <div style={{ marginBottom: 16 }}>
 <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8 }}>Método de pagamento</div>
 <div style={{ display: 'flex', gap: 8 }}>
 {[
 { id: 'pix' as const, label: i18next.t('billing:pix_method'), desc: i18next.t('billing:pix_desc') },
 { id: 'credit_card' as const, label: i18next.t('billing:credit_card_method'), desc: i18next.t('common:profile.credit_debit') },
 ].map(m => (
 <div key={m.id} onClick={() => { topupMethod.value = m.id; topupResult.value = null; topupError.value = ''; topupPolling.value = false; }} style={{
 flex: 1, padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
 background: topupMethod.value === m.id ? 'rgba(124,92,252,0.1)' : T.bgCard,
 border: `1px solid ${topupMethod.value === m.id ? T.accent : T.border}`,
 transition: 'all 0.2s',
 }}>
 <div style={{ fontSize: 14, fontWeight: 600, color: topupMethod.value === m.id ? T.accent : T.textSecondary }}>{m.label}</div>
 <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{m.desc}</div>
 </div>
 ))}
 </div>
 </div>

 {/* Card form */}
 {topupMethod.value === 'credit_card' && (!topupResult.value || topupResult.value.status === 'paid') && (
 <div style={{ marginBottom: 16, padding: 16, background: 'rgba(255,255,255,0.02)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)' }}>
 <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>Dados do cartão</div>

 {/* Saved cards */}
 {savedCards.value.length > 0 && (
 <div style={{ marginBottom: 12 }}>
 <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 8 }}>Cartões salvos</div>
 <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
 {savedCards.value.map(card => (
 <div key={card.id} onClick={() => { selectedSavedCard.value = selectedSavedCard.value === card.id ? null : card.id; }}
 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: selectedSavedCard.value === card.id ? 'rgba(124,92,252,0.1)' : T.bg, border: `1px solid ${selectedSavedCard.value === card.id ? T.accent : T.borderHover}`, borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s' }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
 <span style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: 'monospace' }}>
 {card.brand.toUpperCase()} **** {card.last4}
 </span>
 <span style={{ fontSize: 11, color: T.textMuted }}>{card.exp_month}/{card.exp_year}</span>
 </div>
 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 {selectedSavedCard.value === card.id && <span style={{ fontSize: 11, color: T.accent, fontWeight: 600 }}>Selecionado</span>}
 <Button variant="ghost" onClick={(e) => { e.stopPropagation(); if (confirm(i18next.t('common:profile.remove_saved_card'))) { api.delete(`/wallet/cards/${card.id}`).then(() => fetchWalletData()); }}}
 style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 11, cursor: 'pointer', padding: '2px 6px' }}>Remover</Button>
 </div>
 </div>
 ))}
 </div>
 {!selectedSavedCard.value && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8, textAlign: 'center' }}>Ou insira um novo cartão abaixo</div>}
 </div>
 )}

 {/* New card form (hidden when using saved card) */}
 {!selectedSavedCard.value && (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
 <input placeholder="Numero do cartão" value={cardNumber.value} inputMode="numeric" autoComplete="cc-number"
 onInput={(e: any) => {
 const raw = e.target.value.replace(/\D/g, '').slice(0, 16);
 cardNumber.value = raw.replace(/(\d{4})(?=\d)/g, '$1 ');
 if (raw.length >= 16) { const next = e.target.parentElement.querySelector('[data-field="name"]'); if (next) next.focus(); }
 }}
 style={{ width: '100%', height: 44, padding: '0 14px', background: T.bg, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: T.text, fontSize: 15, fontFamily: 'monospace', outline: 'none', letterSpacing: 2, boxSizing: 'border-box' } as any}
 onFocus={(e: any) => { e.target.style.borderColor = T.accent; }} onBlur={(e: any) => { e.target.style.borderColor = T.borderHover; }}
 />
 <input placeholder="NOME NO CARTAO" data-field="name" value={cardName.value} autoComplete="cc-name"
 onInput={(e: any) => { cardName.value = e.target.value.toUpperCase(); }}
 style={{ width: '100%', height: 44, padding: '0 14px', background: T.bg, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: T.text, fontSize: 14, outline: 'none', textTransform: 'uppercase', boxSizing: 'border-box' } as any}
 onFocus={(e: any) => { e.target.style.borderColor = T.accent; }} onBlur={(e: any) => { e.target.style.borderColor = T.borderHover; }}
 />
 <div style={{ display: 'flex', gap: 10 }}>
 <div style={{ flex: 2, display: 'flex', alignItems: 'center', background: T.bg, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, overflow: 'hidden' }}>
 <input placeholder="MM" maxLength={2} value={cardExpMonth.value} inputMode="numeric" autoComplete="cc-exp-month"
 onInput={(e: any) => {
 const v = e.target.value.replace(/\D/g, '').slice(0, 2);
 cardExpMonth.value = v;
 if (v.length === 2) { const next = e.target.parentElement.querySelector('[data-field="year"]'); if (next) next.focus(); }
 }}
 style={{ flex: 1, height: 44, padding: '0 14px', background: 'transparent', border: 'none', color: T.text, fontSize: 15, textAlign: 'center', outline: 'none', fontFamily: 'monospace' } as any}
 onFocus={(e: any) => { e.target.parentElement.style.borderColor = T.accent; }} onBlur={(e: any) => { e.target.parentElement.style.borderColor = T.borderHover; }}
 />
 <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 18, userSelect: 'none' }}>/</span>
 <input placeholder="AA" data-field="year" maxLength={2} value={cardExpYear.value} inputMode="numeric" autoComplete="cc-exp-year"
 onInput={(e: any) => {
 const v = e.target.value.replace(/\D/g, '').slice(0, 2);
 cardExpYear.value = v;
 if (v.length === 2) { const next = document.querySelector('[data-field="cvv"]') as any; if (next) next.focus(); }
 }}
 style={{ flex: 1, height: 44, padding: '0 14px', background: 'transparent', border: 'none', color: T.text, fontSize: 15, textAlign: 'center', outline: 'none', fontFamily: 'monospace' } as any}
 onFocus={(e: any) => { e.target.parentElement.style.borderColor = T.accent; }} onBlur={(e: any) => { e.target.parentElement.style.borderColor = T.borderHover; }}
 />
 </div>
 <input placeholder="CVV" data-field="cvv" maxLength={4} value={cardCvv.value} inputMode="numeric" autoComplete="cc-csc"
 onInput={(e: any) => { cardCvv.value = e.target.value.replace(/\D/g, '').slice(0, 4); }}
 style={{ flex: 1, height: 44, padding: '0 14px', background: T.bg, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: T.text, fontSize: 15, textAlign: 'center', outline: 'none', fontFamily: 'monospace', boxSizing: 'border-box' } as any}
 onFocus={(e: any) => { e.target.style.borderColor = T.accent; }} onBlur={(e: any) => { e.target.style.borderColor = T.borderHover; }}
 />
 </div>

 {/* Save card checkbox */}
 <label onClick={() => { saveCard.value = !saveCard.value; }} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4 }}>
 <div style={{ width: 18, height: 18, borderRadius: 4, border: `1px solid ${saveCard.value ? T.accent : 'rgba(255,255,255,0.15)'}`, background: saveCard.value ? T.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}>
 {saveCard.value && <span style={{ color: T.text, fontSize: 12, lineHeight: 1 }}>&#10003;</span>}
 </div>
 <span style={{ fontSize: 12, color: T.textSecondary }}>Salvar cartão para compras futuras</span>
 </label>
 </div>
 )}

 <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '10px 12px', background: 'rgba(34,197,94,0.06)', borderRadius: 8, border: '1px solid rgba(34,197,94,0.1)' }}>
 <span style={{ fontSize: 11, color: T.success }}>Pagamento seguro — dados criptografados (AES-256) e processados via Pagar.me</span>
 </div>
 </div>
 )}

 {topupError.value && <div style={{ color: T.danger, fontSize: 13, marginBottom: 12 }}>{topupError.value}</div>}

 {/* QR Code / PIX result */}
 {topupResult.value && topupResult.value.status === 'pending_payment' && topupResult.value.qrCode && (
 <div style={{ textAlign: 'center', padding: 24, background: 'rgba(124,92,252,0.04)', borderRadius: 12, marginBottom: 16, border: '1px solid rgba(124,92,252,0.15)' }}>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>Pagamento PIX</div>
 <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16 }}>$ {topupAmount.value} USD = R$ {(topupAmount.value * usdToBrl.value).toFixed(2)}</div>
 <div style={{ display: 'inline-block', padding: 12, background: '#fff', borderRadius: 10 }}>
 <img src={topupResult.value.qrCode} alt="QR Code PIX" style={{ width: 200, height: 200, display: 'block' }} />
 </div>
 <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
 {topupResult.value.qrCodeText && (
 <Button variant="ghost" onClick={() => { navigator.clipboard.writeText(topupResult.value!.qrCodeText!); }} style={{
 padding: '8px 16px', borderRadius: 8, background: T.accent, color: T.text, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
 }}>Copiar código PIX</Button>
 )}
 <Button variant="ghost" onClick={() => { topupResult.value = null; topupPolling.value = false; }} style={{
 padding: '8px 16px', borderRadius: 8, background: 'transparent', color: T.textSecondary, border: '1px solid rgba(255,255,255,0.1)', fontSize: 12, cursor: 'pointer',
 }}>Cancelar</Button>
 </div>
 {topupPolling.value && (
 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12 }}>
 <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.warning, animation: 'pulse 1s infinite' }} />
 <span style={{ fontSize: 12, color: T.warning }}>Aguardando pagamento...</span>
 </div>
 )}
 </div>
 )}

 {topupResult.value?.status === 'paid' && (
 <div style={{ textAlign: 'center', padding: 16, background: 'rgba(34,197,94,0.1)', borderRadius: 10, marginBottom: 16, border: '1px solid rgba(34,197,94,0.2)' }}>
 <div style={{ fontSize: 16, fontWeight: 600, color: T.success }}>Pagamento confirmado!</div>
 <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 4 }}>$ {topupAmount.value.toFixed(2)} USD adicionados à sua carteira</div>
 </div>
 )}

 {(!topupResult.value || topupResult.value.status === 'paid') && (
 <Button variant="primary" fullWidth onClick={handleTopup} disabled={topupLoading.value}>
 {topupLoading.value ? 'Processando...' : `Adicionar $ ${topupAmount.value.toFixed(2)} USD (R$ ${(topupAmount.value * usdToBrl.value).toFixed(2)})`}
 </Button>
 )}
 </div>)}
 </div>

 {/* MELHORIA 5: Limites de Gasto Mensal */}
 <div style={{ background: T.bg, borderRadius: 12, padding: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
  <div onClick={() => { limitsOpen.value = !limitsOpen.value; }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
   <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: T.text }}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#7c5cfc" strokeWidth="1.8" fill="none"/><path d="M12 8v4l3 3" stroke="#7c5cfc" strokeWidth="1.8" strokeLinecap="round" fill="none"/></svg>
    Limites de Gasto
   </div>
   <span style={{ fontSize: 14, color: T.textSecondary, transition: 'transform 0.2s', display: 'inline-block', transform: limitsOpen.value ? 'rotate(90deg)' : 'rotate(0deg)' }}>▸</span>
  </div>
  {limitsOpen.value && (<div style={{ marginTop: 14 }}>
  <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 14 }}>Defina limites para controlar seus gastos mensais. Soft limit = aviso. Hard limit = bloqueio.</div>
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
   <div>
    <label style={{ fontSize: 12, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Soft Limit (aviso) — USD</label>
    <div style={{ position: 'relative' }}>
     <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: T.textMuted }}>$</span>
     <input type="number" min={0} step={1} value={spendingLimits.value.soft_limit_usd}
      onInput={(e: any) => { spendingLimits.value = { ...spendingLimits.value, soft_limit_usd: Number(e.target.value) }; }}
      style={{ width: '100%', padding: '10px 12px 10px 24px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: T.bg, color: T.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }} />
    </div>
   </div>
   <div>
    <label style={{ fontSize: 12, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Hard Limit (bloqueio) — USD</label>
    <div style={{ position: 'relative' }}>
     <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: T.textMuted }}>$</span>
     <input type="number" min={0} step={1} value={spendingLimits.value.hard_limit_usd}
      onInput={(e: any) => { spendingLimits.value = { ...spendingLimits.value, hard_limit_usd: Number(e.target.value) }; }}
      style={{ width: '100%', padding: '10px 12px 10px 24px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: T.bg, color: T.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }} />
    </div>
   </div>
  </div>
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
   <Button variant="primary" size="sm" onClick={saveSpendingLimits} disabled={spendingLimitsSaving.value}>{spendingLimitsSaving.value ? i18next.t('common:processing') : i18next.t('common:profile.save_limits')}</Button>
   {spendingLimitsMsg.value && <span style={{ fontSize: 12, color: spendingLimitsMsg.value.includes('sucesso') ? T.success : T.danger }}>{spendingLimitsMsg.value}</span>}
  </div>
  </div>)}
 </div>

 {/* MELHORIA 6: Histórico de Transações Melhorado */}
 <div style={{ background: T.bg, borderRadius: 12, padding: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
 <div onClick={() => { txHistoryOpen.value = !txHistoryOpen.value; }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: T.text }}>
   <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="#7c5cfc" strokeWidth="1.8" fill="none"/><line x1="7" y1="8" x2="17" y2="8" stroke="#7c5cfc" strokeWidth="1.5" strokeLinecap="round"/><line x1="7" y1="12" x2="17" y2="12" stroke="#7c5cfc" strokeWidth="1.5" strokeLinecap="round"/><line x1="7" y1="16" x2="13" y2="16" stroke="#7c5cfc" strokeWidth="1.5" strokeLinecap="round"/></svg>
   Histórico de Transações
  </div>
  <span style={{ fontSize: 14, color: T.textSecondary, transition: 'transform 0.2s', display: 'inline-block', transform: txHistoryOpen.value ? 'rotate(90deg)' : 'rotate(0deg)' }}>▸</span>
 </div>

 {txHistoryOpen.value && (<div style={{ marginTop: 12 }}>

 {/* Filtros */}
 <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' as const }}>
  <Select value={txFilterType.value} onChange={(v) => { txFilterType.value = v; txVisibleCount.value = 20; }}
   options={[
   { value: 'all', label: i18next.t('common:profile.all') },
   { value: 'charge', label: i18next.t('common:profile.usage') },
   { value: 'topup', label: i18next.t('common:profile.topup') },
   { value: 'admin_credit', label: i18next.t('common:profile.credit') },
   { value: 'auto_recharge', label: i18next.t('common:profile.auto_recharge') },
  ]} />
  {(['7d', '30d', '90d'] as const).map(p => (
   <Button variant="ghost" key={p} onClick={() => { txFilterPeriod.value = p; txVisibleCount.value = 20; }}
    style={{ padding: '6px 10px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer',
     background: txFilterPeriod.value === p ? T.primary : T.border, color: txFilterPeriod.value === p ? '#fff' : T.textSecondary }}>
    {p}
   </Button>
  ))}
 </div>

 {(() => {
  const TX_ICONS: Record<string, string> = { charge: '', topup: '', auto_recharge: '', admin_credit: '', llm_credit: '', refund: '', deposit: '', earning: '' };
  const now = new Date();
  const periodDays = txFilterPeriod.value === '7d' ? 7 : txFilterPeriod.value === '30d' ? 30 : 90;
  const cutoff = new Date(now.getTime() - periodDays * 86400000);

  let filtered = walletTx.value.filter(tx => new Date(tx.created_at) >= cutoff);
  if (txFilterType.value !== 'all') filtered = filtered.filter(tx => tx.type === txFilterType.value);

  const totalSpent = filtered.filter(tx => tx.amount < 0).reduce((s, tx) => s + Math.abs(tx.amount), 0);
  const totalAdded = filtered.filter(tx => tx.amount > 0).reduce((s, tx) => s + tx.amount, 0);

  // Paginação: limitar transações visíveis
  const visible = filtered.slice(0, txVisibleCount.value);

  // Agrupar por dia
  const grouped: Record<string, typeof filtered> = {};
  for (const tx of visible) {
   const dayKey = new Date(tx.created_at).toLocaleDateString('pt-BR');
   if (!grouped[dayKey]) grouped[dayKey] = [];
   grouped[dayKey]!.push(tx);
  }
  const dayKeys = Object.keys(grouped);
  const todayStr = now.toLocaleDateString('pt-BR');
  const yesterdayStr = new Date(now.getTime() - 86400000).toLocaleDateString('pt-BR');

  return (
   <>
    {/* Sumário */}
    <div style={{ display: 'flex', gap: 12, marginBottom: 14, fontSize: 12, color: T.textSecondary, flexWrap: 'wrap' as const }}>
     <span>{filtered.length} transações</span>
     <span>· ${totalSpent.toFixed(2)} gastos</span>
     <span>· ${totalAdded.toFixed(2)} recargas</span>
    </div>

    {filtered.length === 0 ? (
     <div style={{ textAlign: 'center', padding: 24, color: T.textMuted }}>Nenhuma transação no período</div>
    ) : (
     <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 4 }}>
      {dayKeys.map(dayKey => {
       const dayTxs = grouped[dayKey]!;
       const dayTotal = dayTxs.reduce((s, tx) => s + Math.abs(tx.amount), 0);
       const dayLabel = dayKey === todayStr ? i18next.t('chat:today') : dayKey === yesterdayStr ? i18next.t('common:yesterday') : dayKey;
       return (
        <div key={dayKey}>
         <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, padding: '8px 0 4px', borderBottom: '1px solid rgba(255,255,255,0.04)', marginBottom: 4 }}>
          {dayLabel} · ${dayTotal.toFixed(4)}
         </div>
         {dayTxs.map(tx => {
          const info = TX_COLORS[tx.type] || { label: tx.type, color: T.textSecondary };
          const icon = TX_ICONS[tx.type] || '';
          const isPos = tx.amount > 0;
          return (
           <div key={tx.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 6, marginBottom: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
             <span style={{ fontSize: 14 }}>{icon}</span>
             <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
               <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: `${info.color}22`, color: info.color }}>{info.label}</span>
               <span style={{ fontSize: 12, color: T.textSecondary }}>{tx.description}</span>
              </div>
              <div style={{ fontSize: 10, color: T.textMuted }}>{new Date(tx.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
             </div>
            </div>
            <div style={{ textAlign: 'right' as const }}>
             <div style={{ fontSize: 13, fontWeight: 700, color: isPos ? T.success : T.danger }}>{isPos ? '+' : ''}{tx.amount.toFixed(4)}</div>
             {tx.balance_after != null && (
              <div style={{ fontSize: 10, color: T.textMuted }}>Saldo: $ {tx.balance_after.toFixed(4)}</div>
             )}
            </div>
           </div>
          );
         })}
        </div>
       );
      })}
      {txVisibleCount.value < filtered.length && (
       <Button variant="ghost" onClick={() => { txVisibleCount.value += 20; }}
        style={{ width: '100%', marginTop: 10, padding: '10px 0', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: T.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        Carregar mais ({filtered.length - txVisibleCount.value} restantes)
       </Button>
      )}
     </div>
    )}
   </>
  );
 })()}
 </div>)}
 </div>
 </div>
 );
}

// ── Notification Inbox Tab ──

interface InboxNotification {
  id: string;
  customer_id: string;
  type: string;
  category: string;
  title: string;
  message: string;
  read: number;
  action_url: string | null;
  icon: string | null;
  metadata: string | null;
  created_at: string;
}

const NOTIF_CATEGORIES = [
  { id: 'all', label: i18next.t('common:profile.all_fem') },
  { id: 'llm_limit', label: 'LLM' },
  { id: 'machine_ready', label: i18next.t('common:profile.machines') },
  { id: 'deploy', label: 'Deploy' },
  { id: 'general', label: i18next.t('common:profile.system') },
];

function relativeDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}min atrás`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h atrás`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return 'ontem';
  if (diffD < 30) return `${diffD} dias atrás`;
  return d.toLocaleDateString('pt-BR');
}

function TabNotifications() {
  const { t } = useTranslation('common');
  const [notifications, setNotifications] = useState<InboxNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  const fetchNotifications = async (cat: string, off: number, append = false) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(off) });
      if (cat !== 'all') params.set('category', cat);
      const res = await api.get<{ notifications: InboxNotification[]; total: number }>(`/notification-inbox?${params}`);
      if (append) {
        setNotifications(prev => [...prev, ...(res.notifications || [])]);
      } else {
        setNotifications(res.notifications || []);
      }
      setTotal(res.total || 0);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const fetchUnread = async () => {
    try {
      const res = await api.get<{ count: number }>('/notification-inbox/unread-count');
      setUnreadCount(res.count || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchNotifications(category, 0); fetchUnread(); }, []);

  const changeCategory = (cat: string) => {
    setCategory(cat);
    setOffset(0);
    fetchNotifications(cat, 0);
  };

  const markRead = async (id: string) => {
    await api.patch(`/notification-inbox/${id}/read`, {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: 1 } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await api.patch('/notification-inbox/read-all', {});
    setNotifications(prev => prev.map(n => ({ ...n, read: 1 })));
    setUnreadCount(0);
  };

  const loadMore = () => {
    const newOffset = offset + LIMIT;
    setOffset(newOffset);
    fetchNotifications(category, newOffset, true);
  };

  const cardStyle: any = {
    background: T.bgCard,
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, margin: 0, fontFamily: 'Manrope, sans-serif' }}>
            Notificações
          </h2>
          {unreadCount > 0 && (
            <span style={{
              background: T.purple, color: T.text, fontSize: 11, fontWeight: 700,
              padding: '2px 8px', borderRadius: 10, minWidth: 20, textAlign: 'center' as const,
            }}>
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="ghost"
            onClick={markAllRead}
            style={{
              background: 'rgba(139,92,246,0.15)', color: T.purple, border: '1px solid rgba(139,92,246,0.3)',
              borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            Marcar todas como lidas
          </Button>
        )}
      </div>

      {/* Category Filters */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' as const }}>
        {NOTIF_CATEGORIES.map(cat => {
          const active = category === cat.id;
          return (
            <Button variant="ghost"
              key={cat.id}
              onClick={() => changeCategory(cat.id)}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif', border: 'none',
                background: active ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.04)',
                color: active ? T.purple : T.textSecondary,
                transition: 'all 0.15s',
              }}
            >
              {cat.label}
            </Button>
          );
        })}
      </div>

      {/* Notification List */}
      {loading && notifications.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center' as const, color: T.textMuted, padding: 40 }}>
          <div style={{ padding: 8 }}><SkeletonBar width="50%" height={14} /></div>
        </div>
      ) : notifications.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center' as const, color: T.textMuted, padding: 60 }}>
          <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>—</div>
          <div style={{ fontSize: 14, fontFamily: 'Manrope, sans-serif' }}>Nenhuma notificação</div>
        </div>
      ) : (
        <div>
          {notifications.map(n => (
            <div
              key={n.id}
              onClick={() => { if (!n.read) markRead(n.id); }}
              style={{
                ...cardStyle,
                cursor: n.read ? 'default' : 'pointer',
                display: 'flex', alignItems: 'flex-start', gap: 12,
                opacity: n.read ? 0.6 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              {/* Unread indicator */}
              <div style={{
                width: 8, height: 8, borderRadius: '50%', marginTop: 6, flexShrink: 0,
                background: n.read ? 'transparent' : T.purple,
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 14, fontWeight: n.read ? 500 : 700, color: T.text,
                  fontFamily: 'Manrope, sans-serif', marginBottom: 4,
                }}>
                  {n.title}
                </div>
                <div style={{
                  fontSize: 13, color: T.textSecondary, fontFamily: 'Manrope, sans-serif',
                  lineHeight: '1.4', marginBottom: 6,
                }}>
                  {n.message}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: T.textMuted, fontFamily: 'Manrope, sans-serif' }}>
                    {relativeDate(n.created_at)}
                  </span>
                  {n.category && n.category !== 'general' && (
                    <span style={{
                      fontSize: 10, color: T.purple, background: 'rgba(139,92,246,0.1)',
                      padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                    }}>
                      {n.category}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Load More */}
          {notifications.length < total && (
            <Button variant="ghost"
              onClick={loadMore}
              disabled={loading}
              style={{
                width: '100%', padding: '10px 0', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'Manrope, sans-serif', border: 'none',
                background: 'rgba(255,255,255,0.04)', color: T.textSecondary, marginTop: 8,
              }}
            >
              {loading ? <><Spinner size="sm" inline /> {i18next.t('common:loading')}</> : i18next.t('common:profile.load_more')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Tab: Elon Password

function TabElonPassword() {
  const { t } = useTranslation('common');
 const [view, setView] = useState<'items' | 'tokens' | 'generator' | 'health' | 'api' | 'import'>('items');
 const [items, setItems] = useState<any[]>([]);
 const [tokens, setTokens] = useState<any[]>([]);
 const [folders, setFolders] = useState<any[]>([]);
 const [loading, setLoading] = useState(true);
 const [search, setSearch] = useState('');
 const [selectedCategory, setSelectedCategory] = useState('');
 const [showCreate, setShowCreate] = useState(false);
 const [generatedPw, setGeneratedPw] = useState<any>(null);
 const [healthReport, setHealthReport] = useState<any>(null);
 const [showDetail, setShowDetail] = useState<any>(null);
 const [revealedValue, setRevealedValue] = useState('');
 const [createForm, setCreateForm] = useState({ name: '', value: '', category: 'password', description: '', folder_id: '', tags: '', url: '', notes: '', item_type: 'password' });
 const [createLoading, setCreateLoading] = useState(false);

 const fetchItems = async () => {
  setLoading(true);
  try {
   const params = new URLSearchParams();
   if (search) params.set('search', search);
   if (selectedCategory) params.set('category', selectedCategory);
   const res = await api.get<any>(`/vault/items?${params}`);
   setItems(res?.items || []);
  } catch { }
  setLoading(false);
 };

 const fetchTokens = async () => {
  try {
   const res = await api.get<any>('/vault-tokens/tokens');
   setTokens(res?.tokens || []);
  } catch { }
 };

 const fetchFolders = async () => {
  try {
   const res = await api.get<any>('/vault/folders');
   setFolders(res?.folders || []);
  } catch { }
 };

 useEffect(() => { fetchItems(); fetchFolders(); }, []);

 const generatePassword = async () => {
  try {
   const res = await api.post<any>('/vault/generate/password', { length: 20, uppercase: true, lowercase: true, numbers: true, symbols: true });
   setGeneratedPw(res?.passwords?.[0]);
  } catch { }
 };

 const fetchHealth = async () => {
  try {
   const res = await api.get<any>('/vault/health');
   setHealthReport(res);
  } catch { }
 };

 const viewItem = async (id: string) => {
  try {
   const res = await api.get<any>(`/vault/items/${id}`);
   setShowDetail(res?.item);
   setRevealedValue(res?.item?.decrypted_value || '');
  } catch { }
 };

 const deleteItem = async (id: string) => {
  if (!confirm('Delete this item?')) return;
  try {
   await api.delete(`/vault/items/${id}`);
   setItems(prev => prev.filter(i => i.id !== id));
   setShowDetail(null);
  } catch { }
 };

 const s = {
  card: { background: T.bgCard, borderRadius: 12, padding: 16, border: '1px solid rgba(255,255,255,0.06)' } as const,
  btn: { padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 } as const,
  btnPrimary: { background: 'linear-gradient(135deg, #2F6BFF, #7C3AED)', color: T.text } as const,
  btnGhost: { background: T.border, color: T.textSecondary } as const,
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: T.bgCardHover, color: T.text, fontSize: 14, outline: 'none' } as const,
  badge: (color: string) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: `${color}20`, color }) as const,
 };

 const subTabs = [
  { id: 'items', label: 'Items', count: items.length },
  { id: 'tokens', label: 'API Tokens' },
  { id: 'generator', label: 'Generator' },
  { id: 'health', label: 'Health' },
  { id: 'api', label: 'API' },
  { id: 'import', label: 'Import/Export' },
 ];

 return (
  <div style={{ maxWidth: 1200 }}>
   {/* Sub-tabs */}
   <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
    {subTabs.map(t => (
     <Button variant="ghost" key={t.id} onClick={() => {
      setView(t.id as any);
      if (t.id === 'tokens') fetchTokens();
      if (t.id === 'health') fetchHealth();
     }} style={{
      ...s.btn,
      ...(view === t.id ? s.btnPrimary : s.btnGhost),
     }}>
      {t.label} {t.count !== undefined ? `(${t.count})` : ''}
     </Button>
    ))}
   </div>

   {/* Items View */}
   {view === 'items' && (
    <div>
     <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
      <input placeholder="Search items..." value={search} onInput={(e: any) => { setSearch(e.target.value); setTimeout(fetchItems, 300); }} style={{ ...s.input, flex: 1, minWidth: 200 }} />
      <select value={selectedCategory} onChange={(e: any) => { setSelectedCategory(e.target.value); setTimeout(fetchItems, 100); }} style={{ ...s.input, width: 'auto', minWidth: 120 }}>
       <option value="">All Categories</option>
       <option value="password">Password</option>
       <option value="login">Login</option>
       <option value="api_key">API Key</option>
       <option value="ssh_key">SSH Key</option>
       <option value="credit_card">Credit Card</option>
       <option value="secure_note">Secure Note</option>
       <option value="server">Server</option>
       <option value="database">Database</option>
       <option value="crypto_wallet">₿ Crypto</option>
      </select>
      <Button variant="ghost" onClick={() => setShowCreate(true)} style={{ ...s.btn, ...s.btnPrimary }}>+ Add Item</Button>
     </div>

     {loading ? <div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div> : (
      <div style={{ display: 'grid', gap: 8 }}>
       {items.length === 0 && <div style={{ ...s.card, textAlign: 'center', color: T.textSecondary, padding: 40 }}>No items yet. Add your first password! </div>}
       {items.map((item: any) => (
        <div key={item.id} onClick={() => viewItem(item.id)} style={{ ...s.card, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.15s' }}>
         <div>
          <div style={{ fontWeight: 600, color: T.text, fontSize: 14 }}>{item.name}</div>
          <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>
           {getCategoryIcon(item.category)} {item.category} • {new Date(item.updated_at).toLocaleDateString()}
          </div>
         </div>
         <div style={{ display: 'flex', gap: 6 }}>
          {item.is_favorite ? <span></span> : null}
          <span style={s.badge(T.primary)}>{item.item_type || item.category}</span>
         </div>
        </div>
       ))}
      </div>
     )}

     {/* Detail Modal */}
     {showDetail && (
      <Modal open={!!showDetail} onClose={() => setShowDetail(null)} title={showDetail.name} size="md">
        <div style={{ marginBottom: 12 }}>
         <label style={{ fontSize: 12, color: T.textSecondary }}>Value</label>
         <div style={{ ...s.input, fontFamily: 'monospace', wordBreak: 'break-all', marginTop: 4 }}>{revealedValue || '••••••••'}</div>
        </div>
        {showDetail.description && <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 12 }}>{showDetail.description}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
         <Button variant="primary" size="sm" onClick={() => { navigator.clipboard.writeText(revealedValue); }}>Copy</Button>
         <Button variant="danger" size="sm" onClick={() => deleteItem(showDetail.id)}> Delete</Button>
         <Button variant="ghost" size="sm" onClick={() => setShowDetail(null)}>Close</Button>
        </div>
      </Modal>
     )}

     {/* Create Item Modal */}
     {showCreate && (
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add New Item" size="lg">
        <div style={{ display: 'grid', gap: 12 }}>
         <div>
          <label style={{ fontSize: 12, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Name *</label>
          <input value={createForm.name} onInput={(e: any) => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. GitHub Login" style={s.input} />
         </div>
         <div>
          <label style={{ fontSize: 12, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Value (Password / Secret) *</label>
          <input type="password" value={createForm.value} onInput={(e: any) => setCreateForm(f => ({ ...f, value: e.target.value }))} placeholder="Enter password or secret value" style={{ ...s.input, fontFamily: 'monospace' }} />
         </div>
         <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
           <label style={{ fontSize: 12, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Category</label>
           <Select value={createForm.category} onChange={(v) => setCreateForm(f => ({ ...f, category: v, item_type: v }))} options={[
            { value: 'password', label: 'Password' },
            { value: 'login', label: 'Login' },
            { value: 'api_key', label: 'API Key' },
            { value: 'ssh_key', label: 'SSH Key' },
            { value: 'credit_card', label: 'Credit Card' },
            { value: 'secure_note', label: 'Secure Note' },
            { value: 'server', label: 'Server' },
            { value: 'database', label: 'Database' },
            { value: 'crypto_wallet', label: '₿ Crypto Wallet' },
            { value: 'wifi', label: 'WiFi' },
            { value: 'license', label: 'License' },
           ]} />
          </div>
          <div>
           <label style={{ fontSize: 12, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Folder</label>
           <Select value={createForm.folder_id} onChange={(v) => setCreateForm(f => ({ ...f, folder_id: v }))} placeholder="No folder" options={folders.map((f: any) => ({ value: f.id, label: f.name }))} />
          </div>
         </div>
         <div>
          <label style={{ fontSize: 12, color: T.textSecondary, display: 'block', marginBottom: 4 }}>URL</label>
          <input value={createForm.url} onInput={(e: any) => setCreateForm(f => ({ ...f, url: e.target.value }))} placeholder="https://github.com" style={s.input} />
         </div>
         <div>
          <label style={{ fontSize: 12, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Description</label>
          <input value={createForm.description} onInput={(e: any) => setCreateForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description" style={s.input} />
         </div>
         <div>
          <label style={{ fontSize: 12, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Tags (comma separated)</label>
          <input value={createForm.tags} onInput={(e: any) => setCreateForm(f => ({ ...f, tags: e.target.value }))} placeholder="work, dev, personal" style={s.input} />
         </div>
         <div>
          <label style={{ fontSize: 12, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Notes</label>
          <Textarea value={createForm.notes} onInput={(e: any) => setCreateForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes..." rows={3} style={{ ...s.input, resize: 'vertical' } as any} />
         </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
         <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
         <Button variant="primary" disabled={createLoading || !createForm.name || !createForm.value} onClick={async () => {
          setCreateLoading(true);
          try {
           const body: any = { name: createForm.name, value: createForm.value, category: createForm.category, item_type: createForm.item_type };
           if (createForm.description) body.description = createForm.description;
           if (createForm.folder_id) body.folder_id = createForm.folder_id;
           if (createForm.url) body.url = createForm.url;
           if (createForm.notes) body.notes = createForm.notes;
           if (createForm.tags) body.tags = createForm.tags.split(',').map((t: string) => t.trim()).filter(Boolean);
           await api.post('/vault/items', body);
           setShowCreate(false);
           setCreateForm({ name: '', value: '', category: 'password', description: '', folder_id: '', tags: '', url: '', notes: '', item_type: 'password' });
           fetchItems();
          } catch (err: any) { alert('Failed to create item: ' + (err.message || 'Unknown error')); }
          setCreateLoading(false);
         }}>
          {createLoading ? 'Creating...' : 'Save Item'}
         </Button>
        </div>
      </Modal>
     )}
    </div>
   )}

   {/* Generator View */}
   {view === 'generator' && (
    <div style={s.card}>
     <h3 style={{ color: T.text, margin: '0 0 16px' }}>Password Generator</h3>
     <Button variant="ghost" onClick={generatePassword} style={{ ...s.btn, ...s.btnPrimary, marginBottom: 16 }}>Generate Password</Button>
     {generatedPw && (
      <div>
       <div style={{ ...s.input, fontFamily: 'monospace', fontSize: 18, letterSpacing: 1, marginBottom: 12, wordBreak: 'break-all' }}>{generatedPw.password}</div>
       <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={s.badge(generatedPw.strength === 'very_strong' ? T.success : generatedPw.strength === 'strong' ? T.primary : T.warning)}>
         {generatedPw.strength.replace('_', ' ')}
        </span>
        <span style={{ fontSize: 12, color: T.textSecondary }}>Entropy: {generatedPw.entropy} bits</span>
        <span style={{ fontSize: 12, color: T.textSecondary }}>Crack time: {generatedPw.crack_time_display}</span>
       </div>
       <Button variant="ghost" onClick={() => navigator.clipboard.writeText(generatedPw.password)} style={{ ...s.btn, ...s.btnPrimary }}>Copy</Button>
      </div>
     )}
    </div>
   )}

   {/* API Tokens View */}
   {view === 'tokens' && (
    <div>
     <div onClick={() => activeTab.value = 'api'} style={{ ...s.card, marginBottom: 16, cursor: 'pointer', background: 'rgba(47,107,255,0.04)', border: '1px solid rgba(47,107,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
       <div style={{ fontSize: 13, fontWeight: 600, color: T.primary }}>API</div>
       <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>Manage all tokens & API docs in the API tab →</div>
      </div>
      <span style={{ color: T.primary, fontSize: 18 }}>→</span>
     </div>
     <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
      <h3 style={{ color: T.text, margin: 0 }}>API Tokens (evp_live_xxx)</h3>
      <Button variant="ghost" onClick={async () => {
       const name = prompt('Token name:');
       if (!name) return;
       try {
        const res = await api.post<any>('/vault-tokens/tokens', { name, scopes: ['read', 'write'] });
        const token = res?.plaintext_token;
        if (token) { alert(`Token created! Save it now:\n\n${token}\n\nThis will NOT be shown again.`); fetchTokens(); }
       } catch { }
      }} style={{ ...s.btn, ...s.btnPrimary }}>+ Create Token</Button>
     </div>
     <div style={{ display: 'grid', gap: 8 }}>
      {tokens.length === 0 && <div style={{ ...s.card, textAlign: 'center', color: T.textSecondary, padding: 24 }}>No API tokens yet. Create one to access your vault via API.</div>}
      {tokens.map((t: any) => (
       <div key={t.id} style={{ ...s.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
         <div style={{ fontWeight: 600, color: T.text }}>{t.name}</div>
         <div style={{ fontSize: 12, color: T.textSecondary, fontFamily: 'monospace' }}>{t.token_prefix}••••••••</div>
         <div style={{ fontSize: 11, color: 'rgba(167,179,199,0.5)', marginTop: 2 }}>
          {t.scopes?.join(', ')} • {t.access_level} • Created {new Date(t.created_at).toLocaleDateString()}
          {t.last_used_at && ` • Last used ${new Date(t.last_used_at).toLocaleDateString()}`}
         </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
         <span style={s.badge(t.status === 'active' ? T.success : T.danger)}>{t.status}</span>
         {t.status === 'active' && (
          <Button variant="ghost" onClick={async () => {
           if (!confirm('Revoke this token?')) return;
           try { await api.delete(`/vault-tokens/tokens/${t.id}`); fetchTokens(); } catch { }
          }} style={{ ...s.btn, background: 'rgba(255,59,48,0.1)', color: T.danger, fontSize: 11, padding: '4px 10px' }}>Revoke</Button>
         )}
        </div>
       </div>
      ))}
     </div>
    </div>
   )}

   {/* Health View */}
   {view === 'health' && (
    <div>
     <h3 style={{ color: T.text, margin: '0 0 16px' }}>Password Health Report</h3>
     {!healthReport ? <div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div> : (
      <div>
       <div style={{ ...s.card, textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 48, fontWeight: 800, color: healthReport.score >= 90 ? T.success : healthReport.score >= 70 ? T.primary : healthReport.score >= 50 ? T.warning : T.danger }}>
         {healthReport.score}
        </div>
        <div style={{ fontSize: 14, color: T.textSecondary }}>Security Score</div>
        <div style={s.badge(healthReport.score >= 70 ? T.success : T.warning)}>{healthReport.rating}</div>
       </div>
       <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 16 }}>
        <div style={s.card}><div style={{ fontSize: 24, fontWeight: 700, color: T.text }}>{healthReport.total_items}</div><div style={{ fontSize: 12, color: T.textSecondary }}>Total Items</div></div>
        <div style={s.card}><div style={{ fontSize: 24, fontWeight: 700, color: T.danger }}>{healthReport.reused_passwords}</div><div style={{ fontSize: 12, color: T.textSecondary }}>Reused</div></div>
        <div style={s.card}><div style={{ fontSize: 24, fontWeight: 700, color: T.warning }}>{healthReport.expired_items}</div><div style={{ fontSize: 12, color: T.textSecondary }}>Expired</div></div>
        <div style={s.card}><div style={{ fontSize: 24, fontWeight: 700, color: T.warning }}>{healthReport.old_items}</div><div style={{ fontSize: 12, color: T.textSecondary }}>90+ days old</div></div>
       </div>
       {healthReport.items_with_issues?.length > 0 && (
        <div>
         <h4 style={{ color: T.text, margin: '0 0 8px' }}>Items with Issues</h4>
         {healthReport.items_with_issues.map((item: any) => (
          <div key={item.id} style={{ ...s.card, marginBottom: 4, padding: 10 }}>
           <span style={{ fontWeight: 600, color: T.text }}>{item.name}</span>
           <span style={{ fontSize: 12, color: T.warning, marginLeft: 8 }}>{item.issues.join(', ')}</span>
          </div>
         ))}
        </div>
       )}
      </div>
     )}
    </div>
   )}

   {/* Developer Docs View — Redirects to API */}
   {view === 'api' && (() => {
    // Redirect to centralized API
    return (
     <div onClick={() => activeTab.value = 'api'} style={{ ...s.card, cursor: 'pointer', textAlign: 'center' as const, padding: '32px 24px', background: 'rgba(47,107,255,0.04)', border: '1px solid rgba(47,107,255,0.15)' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}></div>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8 }}>API</div>
      <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>API documentation, token management, and playground have moved to the API tab.</div>
      <div style={{ ...s.btn, ...s.btnPrimary, display: 'inline-block', padding: '10px 24px' }}>Go to API →</div>
     </div>
    );
   })()}

   {/* LEGACY developer docs removed — now in API tab */}
   {false && (() => {
    const copyBtn = (text: string) => (
     <Button variant="ghost" onClick={() => navigator.clipboard.writeText(text)} style={{ ...s.btn, ...s.btnGhost, fontSize: 11, padding: '2px 8px', marginLeft: 8, flexShrink: 0 }}></Button>
    );
    const codeBlock = (code: string) => (
     <div style={{ position: 'relative', marginTop: 8 }}>
      <pre style={{ ...s.input, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.5, margin: 0, padding: 12 }}>{code}</pre>
      <div style={{ position: 'absolute', top: 4, right: 4 }}>{copyBtn(code)}</div>
     </div>
    );
    const endpointCard = (method: string, path: string, desc: string, curl: string, body?: string) => (
     <div style={{ ...s.card, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
       <span style={s.badge(method === 'GET' ? T.success : method === 'POST' ? T.primary : method === 'PUT' ? T.warning : T.danger)}>{method}</span>
       <code style={{ fontSize: 13, color: T.text, fontFamily: 'monospace' }}>{path}</code>
      </div>
      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>{desc}</div>
      {body && <div style={{ fontSize: 11, color: T.textSecondary }}>Body: <code style={{ color: T.purple }}>{body}</code></div>}
      {codeBlock(curl)}
     </div>
    );
    const BASE = '/api/v1';
    const H = '-H "Authorization: Bearer evp_live_YOUR_TOKEN"';
    return (
     <div>
      <h3 style={{ color: T.text, margin: '0 0 16px' }}>Elon Password API Documentation</h3>

      {/* Auth */}
      <div style={{ ...s.card, marginBottom: 16 }}>
       <h4 style={{ color: T.text, margin: '0 0 8px' }}>Authentication</h4>
       <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 8 }}>
        All requests require a Bearer token in the <code style={{ color: T.purple }}>Authorization</code> header.
        Create tokens in the <Button variant="ghost" onClick={() => { setView('tokens'); fetchTokens(); }} style={{ background: 'none', border: 'none', color: T.primary, cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}>API Tokens</Button> tab.
       </div>
       {codeBlock(`Authorization: Bearer evp_live_YOUR_TOKEN`)}
       <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 12 }}>
        <strong style={{ color: T.text }}>Base URL:</strong> <code style={{ color: T.purple }}>{BASE}/vault</code>
       </div>
      </div>

      {/* Vault Items */}
      <h4 style={{ color: T.text, margin: '0 0 8px' }}>Vault Items</h4>
      {endpointCard('GET', '/vault/items', 'List all items (with optional search, category, folder filters)', `curl ${BASE}/vault/items ${H}`)}
      {endpointCard('GET', '/vault/items/:id', 'Get single item with decrypted value', `curl ${BASE}/vault/items/ITEM_ID ${H}`)}
      {endpointCard('POST', '/vault/items', 'Create a new item', `curl -X POST ${BASE}/vault/items ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"My Login","value":"s3cret","category":"password"}'`, '{ name, value, category, description?, folder_id?, tags?, url?, notes?, item_type? }')}
      {endpointCard('PUT', '/vault/items/:id', 'Update an existing item', `curl -X PUT ${BASE}/vault/items/ITEM_ID ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"Updated Name"}'`)}
      {endpointCard('DELETE', '/vault/items/:id', 'Delete an item', `curl -X DELETE ${BASE}/vault/items/ITEM_ID ${H}`)}
      {endpointCard('GET', '/vault/items/:id/versions', 'Get version history for an item', `curl ${BASE}/vault/items/ITEM_ID/versions ${H}`)}

      {/* Generator */}
      <h4 style={{ color: T.text, margin: '16px 0 8px' }}>Generator</h4>
      {endpointCard('POST', '/vault/generate/password', 'Generate random password', `curl -X POST ${BASE}/vault/generate/password ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{"length":20,"uppercase":true,"lowercase":true,"numbers":true,"symbols":true}'`)}
      {endpointCard('POST', '/vault/generate/passphrase', 'Generate passphrase', `curl -X POST ${BASE}/vault/generate/passphrase ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{"words":4,"separator":"-"}'`)}
      {endpointCard('POST', '/vault/generate/pin', 'Generate PIN code', `curl -X POST ${BASE}/vault/generate/pin ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{"length":6}'`)}

      {/* Security */}
      <h4 style={{ color: T.text, margin: '16px 0 8px' }}>Security</h4>
      {endpointCard('POST', '/vault/check-strength', 'Check password strength', `curl -X POST ${BASE}/vault/check-strength ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{"password":"MyP@ssw0rd!"}'`)}
      {endpointCard('POST', '/vault/check-breach', 'Check if password was in a data breach (HIBP)', `curl -X POST ${BASE}/vault/check-breach ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{"password":"password123"}'`)}
      {endpointCard('GET', '/vault/health', 'Get vault health report (score, issues)', `curl ${BASE}/vault/health ${H}`)}

      {/* Metadata */}
      <h4 style={{ color: T.text, margin: '16px 0 8px' }}>Metadata</h4>
      {endpointCard('GET', '/vault/item-types', 'List available item types', `curl ${BASE}/vault/item-types ${H}`)}
      {endpointCard('GET', '/vault/folders', 'List folders', `curl ${BASE}/vault/folders ${H}`)}
      {endpointCard('POST', '/vault/folders', 'Create a folder', `curl -X POST ${BASE}/vault/folders ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"Work"}'`)}

      {/* Import/Export */}
      <h4 style={{ color: T.text, margin: '16px 0 8px' }}>Import / Export</h4>
      {endpointCard('POST', '/vault/import', 'Import items (Bitwarden, Chrome, LastPass, 1Password)', `curl -X POST ${BASE}/vault/import ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{"format":"chrome","data":"csv_content_here"}'`)}
      {endpointCard('GET', '/vault/export', 'Export all items', `curl ${BASE}/vault/export?format=json ${H}`)}

      {/* Token Management */}
      <h4 style={{ color: T.text, margin: '16px 0 8px' }}> Token Management</h4>
      {endpointCard('GET', '/vault-tokens/tokens', 'List all API tokens', `curl ${BASE}/vault-tokens/tokens ${H}`)}
      {endpointCard('POST', '/vault-tokens/tokens', 'Create a new API token', `curl -X POST ${BASE}/vault-tokens/tokens ${H} \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"My CLI Token","scopes":["read","write"],"access_level":"account"}'`, '{ name, scopes: ["read","write"], access_level: "account"|"agent"|"machine"|"all" }')}
      {endpointCard('DELETE', '/vault-tokens/tokens/:id', 'Revoke a token', `curl -X DELETE ${BASE}/vault-tokens/tokens/TOKEN_ID ${H}`)}

      {/* Code Examples */}
      <h4 style={{ color: T.text, margin: '16px 0 8px' }}>Code Examples</h4>
      <div style={{ ...s.card, marginBottom: 8 }}>
       <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={s.badge('#F7DF1E')}>JavaScript / Node.js</span>
       </div>
       {codeBlock(`const TOKEN = 'evp_live_YOUR_TOKEN';
const BASE = '${BASE}/vault';

// List items
const res = await fetch(\`\${BASE}/items\`, {
  headers: { 'Authorization': \`Bearer \${TOKEN}\` }
});
const { items } = await res.json();

// Create item
await fetch(\`\${BASE}/items\`, {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${TOKEN}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'GitHub Token',
    value: 'ghp_xxxxxxxxxxxx',
    category: 'api_key'
  })
});

// Generate password
const pw = await fetch(\`\${BASE}/generate/password\`, {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${TOKEN}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ length: 24, symbols: true })
}).then(r => r.json());`)}
      </div>
      <div style={{ ...s.card, marginBottom: 8 }}>
       <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={s.badge('#3776AB')}>Python</span>
       </div>
       {codeBlock(`import requests

TOKEN = 'evp_live_YOUR_TOKEN'
BASE = '${BASE}/vault'
HEADERS = {'Authorization': f'Bearer {TOKEN}'}

# List items
items = requests.get(f'{BASE}/items', headers=HEADERS).json()['items']

# Create item
requests.post(f'{BASE}/items', headers=HEADERS, json={
    'name': 'GitHub Token',
    'value': 'ghp_xxxxxxxxxxxx',
    'category': 'api_key'
})

# Check password strength
strength = requests.post(f'{BASE}/check-strength',
    headers=HEADERS,
    json={'password': 'MyP@ssw0rd!'}
).json()`)}
      </div>

      {/* Rate Limits & Scopes */}
      <h4 style={{ color: T.text, margin: '16px 0 8px' }}>Rate Limits & Scopes</h4>
      <div style={{ ...s.card, marginBottom: 8 }}>
       <div style={{ display: 'grid', gap: 12 }}>
        <div>
         <strong style={{ color: T.text, fontSize: 13 }}>Rate Limits</strong>
         <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>100 requests/minute per token. Exceeded limits return <code style={{ color: T.danger }}>429 Too Many Requests</code>.</div>
        </div>
        <div>
         <strong style={{ color: T.text, fontSize: 13 }}>Scopes</strong>
         <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <span style={s.badge(T.success)}>read</span>
          <span style={{ fontSize: 12, color: T.textSecondary }}>GET endpoints (list, view, export, health)</span>
         </div>
         <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <span style={s.badge(T.primary)}>write</span>
          <span style={{ fontSize: 12, color: T.textSecondary }}>POST/PUT/DELETE endpoints (create, update, delete, import)</span>
         </div>
        </div>
        <div>
         <strong style={{ color: T.text, fontSize: 13 }}>Access Levels</strong>
         <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
          {['account', 'agent', 'machine', 'all'].map(level => (
           <span key={level} style={s.badge(T.purple)}>{level}</span>
          ))}
         </div>
         <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>
          <strong>account</strong> — Full access to your vault. <strong>agent</strong> — AI agent access. <strong>machine</strong> — CI/CD & automation. <strong>all</strong> — Unrestricted.
         </div>
        </div>
       </div>
      </div>
     </div>
    );
   })()}

   {/* Import/Export View */}
   {view === 'import' && (
    <div>
     <h3 style={{ color: T.text, margin: '0 0 16px' }}>Import / Export</h3>
     <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div style={s.card}>
       <h4 style={{ color: T.text, margin: '0 0 12px' }}>Import</h4>
       <p style={{ fontSize: 13, color: T.textSecondary, margin: '0 0 12px' }}>Import from LastPass, Bitwarden, 1Password, or Chrome CSV.</p>
       <input type="file" accept=".csv,.json" onChange={async (e: any) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const text = await file.text();
        const format = file.name.endsWith('.json') ? 'bitwarden' : 'chrome';
        try {
         const res = await api.post<any>('/vault/import', { format, data: text });
         alert(`Imported ${res?.imported || 0} items. Skipped: ${res?.skipped || 0}`);
         fetchItems();
        } catch (err: any) { alert('Import failed: ' + (err.message || 'Unknown error')); }
       }} style={{ marginBottom: 8 }} />
       <div style={{ fontSize: 11, color: 'rgba(167,179,199,0.5)' }}>Supported: .csv (Chrome/LastPass), .json (Bitwarden/1Password)</div>
      </div>
      <div style={s.card}>
       <h4 style={{ color: T.text, margin: '0 0 12px' }}>Export</h4>
       <p style={{ fontSize: 13, color: T.textSecondary, margin: '0 0 12px' }}>Download all vault items.</p>
       <div style={{ display: 'flex', gap: 8 }}>
        <a href="/api/v1/vault/export?format=json" download style={{ ...s.btn, ...s.btnPrimary, textDecoration: 'none', textAlign: 'center' }}>JSON</a>
        <a href="/api/v1/vault/export?format=csv" download style={{ ...s.btn, ...s.btnGhost, textDecoration: 'none', textAlign: 'center' }}>CSV</a>
       </div>
      </div>
     </div>
    </div>
   )}
  </div>
 );
}

function getCategoryIcon(category: string): string {
 const icons: Record<string, string> = {
  password: '', login: '', ssh_key: '', api_key: '',
  credit_card: '', bank_account: '', wifi: '', identity: '',
  secure_note: '', crypto_wallet: '', server: '', database: '',
  license: '',
 };
 return icons[category] || '';
}

// ═══════════════════════════════════════════════════════════════════════
// TabAPI — API Dashboard (Big Tech Style)
// ═══════════════════════════════════════════════════════════════════════

function TabAPI() {
 const [allTokens, setAllTokens] = useState<any[]>([]);
 const [tokensLoading, setTokensLoading] = useState(false);
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [createdToken, setCreatedToken] = useState('');
 const [activeSection, setActiveSection] = useState<'keys' | 'usage' | 'quickstart' | 'endpoints'>('keys');
 const [codeTab, setCodeTab] = useState<'curl' | 'python' | 'nodejs'>('curl');
 const [usagePeriod, setUsagePeriod] = useState<'7d' | '30d' | '90d'>('30d');
 const [usageData, setUsageData] = useState<any>(null);
 const [creditsData, setCreditsData] = useState<any>(null);

 // Playground state
 const [pgEndpoint, setPgEndpoint] = useState('/chat/completions');
 const [pgBody, setPgBody] = useState(JSON.stringify({ model: 'claude-sonnet-4.5', messages: [{ role: 'user', content: 'Olá!' }] }, null, 2));
 const [pgToken, setPgToken] = useState('');
 const [pgResponse, setPgResponse] = useState<any>(null);
 const [pgLoading, setPgLoading] = useState(false);
 const [showPlayground, setShowPlayground] = useState(false);

 const fetchAllTokens = async () => {
  setTokensLoading(true);
  try {
   const res = await api.getQuiet<any>('/router/tokens');
   const tokens = (res?.tokens || []).map((t: any) => ({ ...t, _type: 'router' as const }));
   setAllTokens(tokens);
  } catch {}
  setTokensLoading(false);
 };

 const fetchUsage = async () => {
  try {
   const res = await api.getQuiet<any>('/router/my-usage');
   setUsageData(res);
  } catch {}
 };

 const fetchCredits = async () => {
  try {
   const res = await api.getQuiet<any>('/router/my-credits');
   setCreditsData(res);
  } catch {}
 };

 useEffect(() => { fetchAllTokens(); fetchUsage(); fetchCredits(); }, []);

 const activeTokens = allTokens.filter(t => t.status === 'active' || (t.is_active && !t.revoked_at));
 const copyToClipboard = (text: string) => { navigator.clipboard.writeText(text); };

 const typeBadge = (type: string) => {
  const map: Record<string, { label: string; color: string }> = {
   router: { label: 'Router', color: T.primary },
   password: { label: 'Password', color: T.purple },
   elontools: { label: tenantName() || 'Platform', color: T.success }, // tenant-id: internal key
  };
  const m = map[type] || { label: type, color: T.textSecondary };
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: `${m.color}20`, color: m.color }}>{m.label}</span>;
 };

 const statusBadge = (token: any) => {
  const isActive = token.status === 'active' || (token.is_active && !token.revoked_at);
  const color = isActive ? T.success : T.danger;
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: `${color}20`, color }}>{isActive ? 'Active' : 'Revoked'}</span>;
 };

 const codeExamples: Record<string, string> = {
  curl: `curl /api/v1/llm/v1/chat/completions \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "claude-sonnet-4.5",
    "messages": [{"role": "user", "content": "Hello!"}]
  }'`,
  python: `from openai import OpenAI

client = OpenAI(
    api_key="YOUR_API_KEY",
    base_url="/api/v1/llm/v1"
)

response = client.chat.completions.create(
    model="claude-sonnet-4.5",
    messages=[{"role": "user", "content": "Hello!"}]
)
print(response.choices[0].message.content)`,
  nodejs: `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: "YOUR_API_KEY",
  baseURL: "/api/v1/llm/v1"
});

const response = await client.chat.completions.create({
  model: "claude-sonnet-4.5",
  messages: [{ role: "user", content: "Hello!" }]
});
console.log(response.choices[0].message.content);`,
 };

 const endpoints = [
  { method: 'POST', path: '/chat/completions', desc: 'Chat with AI models (streaming supported)', color: T.success },
  { method: 'POST', path: '/images/generations', desc: 'Generate images from text prompts', color: T.success },
  { method: 'GET', path: '/models', desc: 'List available models', color: T.primary },
  { method: 'GET', path: '/generation?id={id}', desc: 'Get generation details by ID', color: T.primary },
  { method: 'GET', path: '/credits', desc: 'Check balance and credits', color: T.primary },
 ];

 const navItems = [
  { id: 'keys' as const, label: 'API Keys', icon: '' },
  { id: 'usage' as const, label: 'Usage', icon: '' },
  { id: 'quickstart' as const, label: 'Quick Start', icon: '' },
  { id: 'endpoints' as const, label: 'Endpoints', icon: '' },
 ];

 const cardStyle = { background: T.bgInput, borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', padding: 20 } as const;
 const statCardStyle = { ...cardStyle, padding: '16px 20px', flex: 1, minWidth: 140 } as const;

 return (
  <div style={{ maxWidth: 1200, display: 'flex', flexDirection: 'column' as const, gap: 0 }}>

   {/* ── Header ── */}
   <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 24, fontWeight: 800, color: T.text, letterSpacing: '-0.02em' }}>API</div>
    <div style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6, marginTop: 4 }}>
     Manage your API keys, monitor usage, and integrate with your application.
    </div>
   </div>

   {/* ── Stats Cards ── */}
   <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' as const, marginTop: 16 }}>
    <div style={statCardStyle}>
     <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6 }}>Active Keys</div>
     <div style={{ fontSize: 28, fontWeight: 800, color: T.text }}>{activeTokens.length}</div>
     <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{allTokens.length} total</div>
    </div>
    <div style={statCardStyle}>
     <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6 }}>Balance</div>
     <div style={{ fontSize: 28, fontWeight: 800, color: T.success }}>${creditsData?.balance != null ? Number(creditsData.balance).toFixed(2) : '—'}</div>
     <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>Available credits</div>
    </div>
    <div style={statCardStyle}>
     <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6 }}>Requests (30d)</div>
     <div style={{ fontSize: 28, fontWeight: 800, color: T.text }}>{usageData?.total_requests ?? '—'}</div>
     <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>Total API calls</div>
    </div>
    <div style={statCardStyle}>
     <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6 }}>Cost (30d)</div>
     <div style={{ fontSize: 28, fontWeight: 800, color: T.warning }}>${usageData?.total_cost != null ? Number(usageData.total_cost).toFixed(2) : '—'}</div>
     <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>Total spend</div>
    </div>
   </div>

   {/* ── Section Nav ── */}
   <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: 24 }}>
    {navItems.map(item => (
     <Button variant="ghost" key={item.id} onClick={() => setActiveSection(item.id)} style={{
      padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'transparent',
      color: activeSection === item.id ? T.text : T.textMuted,
      borderBottom: activeSection === item.id ? '2px solid #2F6BFF' : '2px solid transparent',
      marginBottom: -1, transition: 'all 0.15s ease',
     }}>{item.icon} {item.label}</Button>
    ))}
   </div>

   {/* ════════════════════════════════════════════════════════════ */}
   {/* SECTION: API Keys */}
   {/* ════════════════════════════════════════════════════════════ */}
   {activeSection === 'keys' && (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>

     {/* Created token banner */}
     {createdToken && (
      <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10, padding: 16 }}>
       <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.success }}>API Key Created</span>
        <Button variant="ghost" onClick={() => setCreatedToken('')} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 16 }}>×</Button>
       </div>
       <div style={{ fontSize: 12, color: T.warning, marginBottom: 8 }}>Copy now — this key will only be shown once.</div>
       <div style={{ background: T.bg, borderRadius: 8, padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
        <code style={{ fontSize: 12, color: T.success, fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' as const }}>{createdToken}</code>
        <Button variant="ghost" onClick={() => copyToClipboard(createdToken)} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #374151', background: 'transparent', color: T.textSecondary, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' as const }}>Copy</Button>
       </div>
      </div>
     )}

     {/* Header + Create button */}
     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
       <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Your API Keys</div>
       <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>{activeTokens.length} active of {allTokens.length} total keys</div>
      </div>
      <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>+ Create new key</Button>
     </div>

     {/* Token table */}
     {tokensLoading ? <div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div> : allTokens.length === 0 ? (
      <div style={{ ...cardStyle, textAlign: 'center' as const, padding: '48px 24px' }}>
       <div style={{ fontSize: 32, marginBottom: 12 }}></div>
       <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 6 }}>No API keys yet</div>
       <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 16 }}>Create your first API key to start making requests.</div>
       <Button variant="primary" onClick={() => setShowCreateModal(true)}>Create API Key</Button>
      </div>
     ) : (
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
       <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
         <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {['Name', 'Status', 'Key', 'Created', 'Actions'].map((h, i) => (
           <th key={h} style={{ padding: '12px 16px', textAlign: i === 4 ? 'right' : 'left', color: T.textMuted, fontSize: 11, textTransform: 'uppercase' as const, fontWeight: 600, letterSpacing: '0.05em' } as any}>{h}</th>
          ))}
         </tr>
        </thead>
        <tbody>
         {allTokens.map(t => {
          const isActive = t.status === 'active' || (t.is_active && !t.revoked_at);
          return (
           <tr key={`${t._type}-${t.id}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
            <td style={{ padding: '12px 16px', color: T.text, fontWeight: 500 }}>{t.name}</td>
            <td style={{ padding: '12px 16px' }}>{statusBadge(t)}</td>
            <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 12, color: T.textSecondary }}>{t.token_prefix}••••</td>
            <td style={{ padding: '12px 16px', color: T.textMuted, fontSize: 12 }}>{new Date(t.created_at).toLocaleDateString('pt-BR')}</td>
            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
             <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => copyToClipboard(t.token_prefix)} title="Copy prefix" style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: T.textSecondary, fontSize: 11, cursor: 'pointer' }}></Button>
              {isActive && (
               <Button variant="ghost" onClick={async () => {
                if (!confirm('Revoke this key? This action cannot be undone.')) return;
                const endpoint = `/router/tokens/${t.id}`;
                try { await api.delete(endpoint); fetchAllTokens(); } catch {}
               }} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.06)', color: T.danger, fontSize: 11, cursor: 'pointer' }}>Revoke</Button>
              )}
             </div>
            </td>
           </tr>
          );
         })}
        </tbody>
       </table>
      </div>
     )}

     {/* Playground toggle */}
     <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowPlayground(!showPlayground)}>
       <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>🧪 Playground</div>
        <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>Test API endpoints directly from your browser</div>
       </div>
       <span style={{ color: T.textMuted, fontSize: 14 }}>{showPlayground ? '▲' : '▼'}</span>
      </div>

      {showPlayground && (
       <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12, marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 }}>
        <div>
         <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Token</label>
         <Select value={pgToken} onChange={(v) => setPgToken(v)} placeholder="Select a token..." options={activeTokens.map(t => ({ value: t.token_prefix, label: `${t.name} (${t.token_prefix}••••) — ${t._type}` }))} />
        </div>
        <div>
         <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Endpoint</label>
         <input value={pgEndpoint} onInput={(e: any) => setPgEndpoint(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: T.text, fontSize: 13, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' as const }} />
        </div>
        <div>
         <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Body (JSON)</label>
         <Textarea value={pgBody} onInput={(e: any) => setPgBody(e.target.value)} rows={6} style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: T.bg, color: T.text, fontSize: 12, fontFamily: 'monospace', outline: 'none', resize: 'vertical' as const, lineHeight: 1.5, boxSizing: 'border-box' as const }} />
        </div>
        <Button variant="primary" onClick={async () => {
         setPgLoading(true); setPgResponse(null);
         try {
          const res = await api.post<any>(`/llm/v1${pgEndpoint}`, JSON.parse(pgBody || '{}'));
          setPgResponse({ status: 200, data: res });
         } catch (err: any) { setPgResponse({ status: err?.status || 500, data: { error: err?.message || 'Request error' } }); }
         setPgLoading(false);
        }} disabled={pgLoading} style={{ alignSelf: 'flex-start' }}>
         {pgLoading ? 'Sending...' : '▶ Send Request'}
        </Button>
        {pgResponse && (
         <div style={{ marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
           <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Response</span>
           <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: pgResponse.status < 400 ? `${T.success}20` : '#EF444420', color: pgResponse.status < 400 ? T.success : T.danger }}>{pgResponse.status}</span>
           <Button variant="ghost" onClick={() => copyToClipboard(JSON.stringify(pgResponse.data, null, 2))} style={{ padding: '2px 8px', borderRadius: 6, border: '1px solid #374151', background: 'transparent', color: T.textSecondary, fontSize: 11, cursor: 'pointer', marginLeft: 'auto' }}>Copy</Button>
          </div>
          <pre style={{ background: T.bg, borderRadius: 8, padding: 14, fontFamily: 'monospace', fontSize: 12, color: T.text, whiteSpace: 'pre-wrap' as const, wordBreak: 'break-all' as const, lineHeight: 1.5, margin: 0, maxHeight: 300, overflowY: 'auto' as const }}>{JSON.stringify(pgResponse.data, null, 2)}</pre>
         </div>
        )}
       </div>
      )}
     </div>
    </div>
   )}

   {/* ════════════════════════════════════════════════════════════ */}
   {/* SECTION: Usage */}
   {/* ════════════════════════════════════════════════════════════ */}
   {activeSection === 'usage' && (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Usage Overview</div>
      <div style={{ display: 'flex', gap: 0, background: 'rgba(255,255,255,0.04)', borderRadius: 8, overflow: 'hidden' }}>
       {(['7d', '30d', '90d'] as const).map(p => (
        <Button variant="ghost" key={p} onClick={() => setUsagePeriod(p)} style={{
         padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
         background: usagePeriod === p ? 'rgba(47,107,255,0.15)' : 'transparent',
         color: usagePeriod === p ? T.primary : T.textMuted,
        }}>{p}</Button>
       ))}
      </div>
     </div>

     {/* Usage stats */}
     <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
      <div style={cardStyle}>
       <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8 }}>Total Requests</div>
       <div style={{ fontSize: 32, fontWeight: 800, color: T.text }}>{usageData?.total_requests?.toLocaleString() ?? '—'}</div>
      </div>
      <div style={cardStyle}>
       <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8 }}>Total Tokens</div>
       <div style={{ fontSize: 32, fontWeight: 800, color: T.text }}>{usageData?.total_tokens?.toLocaleString() ?? '—'}</div>
      </div>
      <div style={cardStyle}>
       <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8 }}>Total Cost</div>
       <div style={{ fontSize: 32, fontWeight: 800, color: T.warning }}>${usageData?.total_cost != null ? Number(usageData.total_cost).toFixed(2) : '—'}</div>
      </div>
     </div>

     {/* Models breakdown */}
     <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 16 }}>Usage by Model</div>
      {usageData?.by_model && Array.isArray(usageData.by_model) ? (
       <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
        {usageData.by_model.map((m: any) => (
         <div key={m.model} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
           <code style={{ fontSize: 12, color: T.success, fontFamily: 'monospace' }}>{m.model}</code>
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
           <span style={{ fontSize: 12, color: T.textSecondary }}>{m.requests?.toLocaleString() ?? 0} requests</span>
           <span style={{ fontSize: 12, color: T.warning, fontWeight: 600 }}>${Number(m.cost || 0).toFixed(3)}</span>
          </div>
         </div>
        ))}
       </div>
      ) : (
       <div style={{ textAlign: 'center' as const, color: T.textMuted, padding: '24px 0', fontSize: 13 }}>
        No usage data available yet. Start making API calls to see breakdown.
       </div>
      )}
     </div>

     {/* Rate Limits */}
     <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>Rate Limits</div>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>Current limits for your plan</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
       {[
        { label: 'Requests / min', value: '60 RPM', icon: '' },
        { label: 'Tokens / min', value: '100K TPM', icon: '' },
        { label: 'Monthly budget', value: creditsData?.balance != null ? `$${Number(creditsData.balance).toFixed(2)}` : '—', icon: '' },
       ].map(item => (
        <div key={item.label} style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '14px 16px' }}>
         <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>{item.icon} {item.label}</div>
         <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{item.value}</div>
        </div>
       ))}
      </div>
     </div>
    </div>
   )}

   {/* ════════════════════════════════════════════════════════════ */}
   {/* SECTION: Quick Start */}
   {/* ════════════════════════════════════════════════════════════ */}
   {activeSection === 'quickstart' && (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
     <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Quick Start</div>

     {/* Step-by-step */}
     <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 16 }}>Get started in 3 steps</div>
      {[
       { step: '1', title: 'Create an API Key', desc: 'Go to the API Keys tab and click "+ Create new key". Copy the generated key.' },
       { step: '2', title: 'Add credits', desc: 'Go to Wallet and add credits. The router charges per token used.' },
       { step: '3', title: 'Make your first request', desc: 'Use the code examples below with your API key.' },
      ].map(s => (
       <div key={s.step} style={{ display: 'flex', gap: 14, marginBottom: s.step === '3' ? 0 : 16 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(47,107,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
         <span style={{ fontSize: 14, fontWeight: 700, color: T.primary }}>{s.step}</span>
        </div>
        <div>
         <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{s.title}</div>
         <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.5, marginTop: 2 }}>{s.desc}</div>
        </div>
       </div>
      ))}
     </div>

     {/* Base URL */}
     <div style={cardStyle}>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Base URL</div>
      <div style={{ background: T.bg, borderRadius: 8, padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
       <code style={{ fontSize: 13, color: T.success, fontFamily: 'monospace' }}>/api/v1/llm/v1</code>
       <Button variant="ghost" onClick={() => copyToClipboard('/api/v1/llm/v1')} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: T.textSecondary, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>Copy</Button>
      </div>
     </div>

     {/* Code examples */}
     <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Code Examples</div>
      <div style={{ display: 'flex', gap: 0, marginBottom: 0 }}>
       {(['curl', 'python', 'nodejs'] as const).map((tab, i) => (
        <Button variant="ghost" key={tab} onClick={() => setCodeTab(tab)} style={{
         padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
         background: codeTab === tab ? T.bg : 'transparent',
         color: codeTab === tab ? T.text : T.textMuted,
         borderTopLeftRadius: i === 0 ? 8 : 0,
         borderTopRightRadius: i === 2 ? 8 : 0,
        }}>{tab === 'curl' ? 'cURL' : tab === 'python' ? 'Python' : 'Node.js'}</Button>
       ))}
      </div>
      <div style={{ position: 'relative' }}>
       <pre style={{ background: T.bg, borderRadius: '0 8px 8px 8px', padding: 16, fontFamily: 'monospace', fontSize: 12, color: T.text, whiteSpace: 'pre-wrap' as const, wordBreak: 'break-all' as const, lineHeight: 1.6, margin: 0 }}>{codeExamples[codeTab]}</pre>
       <Button variant="ghost" onClick={() => copyToClipboard(codeExamples[codeTab] || '')} style={{ position: 'absolute' as const, top: 10, right: 10, padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(13,17,23,0.8)', color: T.textSecondary, fontSize: 11, cursor: 'pointer' }}>Copy</Button>
      </div>
     </div>

     {/* Compatible tools */}
     <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Compatible Tools</div>
      <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>100% OpenAI-compatible. Works with any tool that supports custom base URLs.</div>
      <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 8 }}>
       {['Cursor', 'VS Code + Continue', 'Cline', 'Aider', 'OpenClaw', 'LangChain', 'LlamaIndex', 'OpenAI SDK', 'Vercel AI SDK', 'LibreChat', 'LobeChat'].map(tool => (
        <span key={tool} style={{ padding: '5px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: T.textSecondary, fontSize: 12, fontWeight: 500 }}>{tool}</span>
       ))}
      </div>
     </div>

     {/* Docs link */}
     <div style={{ ...cardStyle, background: 'rgba(47,107,255,0.04)', border: '1px solid rgba(47,107,255,0.12)', textAlign: 'center' as const, padding: '24px 20px' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>📚 Full Documentation</div>
      <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 14 }}>Complete API reference, authentication guides, and more.</div>
      <a href="/developers" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', padding: '10px 24px', borderRadius: 8, background: T.primary, color: T.text, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>Developer Docs →</a>
     </div>
    </div>
   )}

   {/* ════════════════════════════════════════════════════════════ */}
   {/* SECTION: Endpoints */}
   {/* ════════════════════════════════════════════════════════════ */}
   {activeSection === 'endpoints' && (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
     <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Available Endpoints</div>
      <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>All endpoints use base URL: <code style={{ fontSize: 12, color: T.success }}>/api/v1/llm/v1</code></div>
     </div>

     <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
      {endpoints.map((ep, i) => (
       <div key={ep.path} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: i < endpoints.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: 'monospace', background: `${ep.color}15`, color: ep.color, minWidth: 42, textAlign: 'center' as const }}>{ep.method}</span>
        <code style={{ fontSize: 13, color: T.text, fontFamily: 'monospace', flex: 1 }}>{ep.path}</code>
        <span style={{ fontSize: 12, color: T.textMuted, textAlign: 'right' as const }}>{ep.desc}</span>
       </div>
      ))}
     </div>

     {/* Models */}
     <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>Available Models</div>
      <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 14 }}>Use clean names — the router resolves to the correct provider version automatically.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
       {[
        { name: 'claude-opus-4.6', provider: 'Anthropic', color: T.warningHover },
        { name: 'claude-sonnet-4.5', provider: 'Anthropic', color: T.warningHover },
        { name: 'claude-haiku-3.5', provider: 'Anthropic', color: T.warningHover },
        { name: 'gpt-4o', provider: 'OpenAI', color: T.success },
        { name: 'gpt-4o-mini', provider: 'OpenAI', color: T.success },
        { name: 'gpt-4.1', provider: 'OpenAI', color: T.success },
        { name: 'gemini-2.5-pro', provider: 'Google', color: T.primary },
        { name: 'gemini-2.5-flash', provider: 'Google', color: T.primary },
       ].map(m => (
        <div key={m.name} style={{ background: T.bg, borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
         <code style={{ fontSize: 12, color: T.success, fontFamily: 'monospace' }}>{m.name}</code>
         <span style={{ fontSize: 10, color: m.color, fontWeight: 600 }}>{m.provider}</span>
        </div>
       ))}
      </div>
     </div>

     {/* Response format */}
     <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>Response Format</div>
      <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>Standard OpenAI-compatible response format. Works with any SDK.</div>
      <pre style={{ background: T.bg, borderRadius: 8, padding: 16, fontFamily: 'monospace', fontSize: 12, color: T.text, whiteSpace: 'pre-wrap' as const, lineHeight: 1.5, margin: 0 }}>{`{
  "id": "chatcmpl-abc123",
  "object": "chat.completion",
  "model": "claude-sonnet-4.5",
  "choices": [{
    "index": 0,
    "message": {
      "role": "assistant",
      "content": "Hello! How can I help?"
    },
    "finish_reason": "stop"
  }],
  "usage": {
    "prompt_tokens": 12,
    "completion_tokens": 8,
    "total_tokens": 20
  }
}`}</pre>
     </div>
    </div>
   )}

   {/* ── Create Token Modal ── */}
   {showCreateModal && (() => {
    const [ctType, setCtType] = useState<'router' | 'password' | 'elontools'>('router'); // tenant-id: internal type key
    const [ctName, setCtName] = useState('');
    const [ctLoading, setCtLoading] = useState(false);

    const handleCreate = async () => {
     if (!ctName.trim()) return;
     setCtLoading(true);
     try {
      let res: any;
      if (ctType === 'router') {
       res = await api.post<any>('/router/tokens', { name: ctName, scopes: ['chat', 'models'] });
      } else if (ctType === 'password') {
       res = await api.post<any>('/vault-tokens/tokens', { name: ctName, scopes: ['read', 'write'], access_level: 'account' });
      } else {
       res = await api.post<any>('/api-tokens/tokens', { name: ctName, scopes: ['read', 'write'], access_level: 'account' });
      }
      const plaintext = res?.plaintext_token;
      if (plaintext) {
       setCreatedToken(plaintext);
       setShowCreateModal(false);
       fetchAllTokens();
      }
     } catch (err: any) { alert('Error creating key: ' + (err?.message || 'Unknown error')); }
     setCtLoading(false);
    };

    return (
     <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create API Key" size="md">
       <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 14 }}>
        <UiInput label="Name" value={ctName} onChange={(v) => setCtName(v)} placeholder="e.g., Production, Development..." />
        <Select label="Type" value={ctType} onChange={(v) => setCtType(v)} options={[
          { value: 'router', label: '🔀 Router (ert_live_)' },
          { value: 'password', label: 'Password (evp_live_)' },
          { value: 'elontools', label: `${tenantName() || 'Platform'} (et_live_)` }, // tenant-id: internal value
        ]} />
       </div>
       <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
        <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
        <Button variant="primary" disabled={!ctName.trim() || ctLoading} onClick={handleCreate}>
         {ctLoading ? 'Creating...' : 'Create Key'}
        </Button>
       </div>
     </Modal>
    );
   })()}

  </div>
 );
}
// Tab Plano — Subscription management
// ═══════════════════════════════════════════════════════════════

const planData = signal<{
  subscription: { id: string; plan_id: string; plan: string; status: string; billing_cycle: string; cancel_at_period_end: number; current_period_end: string; workspaces_limit: number; workspaces_used: number; overage_total_usd: number; grace_period_end: string | null } | null;
  plan: { name: string; slug: string; price_usd_monthly: number; price_usd_yearly: number; workspaces_limit: number; features?: string } | null;
} | null>(null);
const planLoading = signal(true);

async function loadPlanData() {
  planLoading.value = true;
  try {
    const res = await api.get<{ subscription: typeof planData.value extends null ? never : NonNullable<typeof planData.value>['subscription']; plan: typeof planData.value extends null ? never : NonNullable<typeof planData.value>['plan'] }>('/subscription/me');
    planData.value = res as any;
  } catch {
    planData.value = null;
  }
  planLoading.value = false;
}

function TabPlano() {
  const { t } = useTranslation('common');
  useEffect(() => { loadPlanData(); }, []);

  if (planLoading.value) return <div style={{ color: T.textSecondary, padding: 20 }}>Loading subscription...</div>;

  const sub = planData.value?.subscription;
  const plan = planData.value?.plan;
  const planName = plan?.name || sub?.plan_id || sub?.plan || null;
  const hasNoPlan = !sub;
  const isFree = !hasNoPlan && (planName || '').toLowerCase() === 'free';
  const isGrace = sub?.grace_period_end ? new Date(sub.grace_period_end) > new Date() : false;

  return (
    <div style={{ padding: '0' }}>
      {/* Grace period warning */}
      {isGrace && (
        <div style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '1.5rem' }}></span>
          <div>
            <div style={{ color: T.warning, fontWeight: 700 }}>Payment Pending</div>
            <div style={{ color: T.textSecondary, fontSize: '0.85rem' }}>
              Your access will expire on {sub?.grace_period_end ? new Date(sub.grace_period_end).toLocaleDateString() : 'soon'}.
              Please update your payment method.
            </div>
          </div>
        </div>
      )}

      {/* Current plan card */}
      <div style={{ background: T.bgInput, borderRadius: 16, padding: 24, border: '1px solid rgba(255,255,255,0.08)', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: '0.8rem', color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 1 }}>Plano Atual</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: hasNoPlan ? T.warning : T.text }}>{hasNoPlan ? '⚠️ Sem plano' : `📦 ${planName}`}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: hasNoPlan ? T.warning : T.success, fontWeight: 700 }}>{hasNoPlan ? '● Sem assinatura' : sub?.status === 'active' ? '● Ativo' : sub?.status === 'past_due' ? '● Pagamento pendente' : `● ${sub?.status || 'Ativo'}`}</div>
            {sub?.billing_cycle && !isFree && <div style={{ color: T.textMuted, fontSize: '0.8rem' }}>Billed {sub.billing_cycle}</div>}
          </div>
        </div>

        {sub?.cancel_at_period_end === 1 && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <span style={{ color: T.danger, fontSize: '0.85rem' }}>Cancels at end of period ({sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : 'N/A'})</span>
          </div>
        )}

        {/* Yearly savings hint */}
        {!isFree && sub?.billing_cycle === 'monthly' && (
          <div style={{ background: 'rgba(16,185,129,0.1)', borderRadius: 8, padding: 10, marginBottom: 16, fontSize: '0.85rem', color: T.success }}>
             Save up to 20% by switching to annual billing →{' '}
            <a href="/upgrade" style={{ color: T.success, textDecoration: 'underline' }}>Ver planos</a>
          </div>
        )}

        {!isFree && sub?.current_period_end && (
          <div style={{ color: T.textMuted, fontSize: '0.85rem' }}>
            Next billing: {new Date(sub.current_period_end).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Usage bars */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Workspaces */}
        <div style={{ background: T.bgInput, borderRadius: 12, padding: 20, border: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ color: T.textSecondary, fontSize: '0.8rem', marginBottom: 8, textTransform: 'uppercase' }}>Workspaces</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: T.text, marginBottom: 8 }}>
            {sub?.workspaces_used || 0} / {sub?.workspaces_limit || 1}
          </div>
          <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 4, height: 6 }}>
            <div style={{
              background: T.primary, borderRadius: 4, height: 6,
              width: `${Math.min(100, ((sub?.workspaces_used || 0) / (sub?.workspaces_limit || 1)) * 100)}%`,
            }} />
          </div>
        </div>

      </div>

      {/* Free upgrade banner */}
      {isFree && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(59,130,246,0.15))',
          borderRadius: 16, padding: 24, border: '1px solid rgba(16,185,129,0.3)', marginBottom: 20, textAlign: 'center',
        }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: T.text, marginBottom: 8 }}>
             Upgrade to unlock more features
          </div>
          <div style={{ color: T.textSecondary, fontSize: '0.9rem', marginBottom: 16 }}>
            Get more workspaces, LLM credits, and premium features
          </div>
          <Button variant="ghost"
            onClick={() => { window.location.href = '/upgrade'; }}
            style={{
              padding: '12px 32px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: T.success, color: T.text, fontWeight: 700, fontSize: '0.95rem',
            }}
          >
            View Plans →
          </Button>
        </div>
      )}

      {/* Action buttons */}
      {!isFree && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Button variant="ghost"
            onClick={() => { window.location.href = '/upgrade'; }}
            style={{
              padding: '10px 20px', borderRadius: 8, border: '1px solid #374151', cursor: 'pointer',
              background: 'rgba(255,255,255,0.08)', color: T.text, fontWeight: 600, fontSize: '0.85rem',
            }}
          >
            Change Plan
          </Button>
          <Button variant="ghost"
            onClick={async () => {
              try {
                const res = await api.post<{ portal_url: string }>('/subscription/portal', {});
                if (res.portal_url) window.location.href = res.portal_url;
              } catch { alert('Could not open billing portal'); }
            }}
            style={{
              padding: '10px 20px', borderRadius: 8, border: '1px solid #374151', cursor: 'pointer',
              background: 'rgba(255,255,255,0.08)', color: T.text, fontWeight: 600, fontSize: '0.85rem',
            }}
          >
            Manage Billing
          </Button>
          {sub?.cancel_at_period_end !== 1 ? (
            <Button
              variant="danger"
              onClick={async () => {
                if (!confirm('Cancel your subscription? You\'ll keep access until the end of your billing period.')) return;
                try {
                  await api.post('/subscription/cancel', {});
                  await loadPlanData();
                } catch { alert('Error canceling'); }
              }}
              style={{ background: 'transparent', border: '1px solid rgba(239,68,68,0.3)', color: T.danger }}
            >
              Cancel Subscription
            </Button>
          ) : (
            <Button
              variant="success"
              onClick={async () => {
                try {
                  await api.post('/subscription/reactivate', {});
                  await loadPlanData();
                } catch { alert('Error reactivating'); }
              }}
              style={{ background: 'transparent', border: '1px solid rgba(16,185,129,0.3)', color: T.success }}
            >
              Reactivate Subscription
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

const PROFILE_TAB_IDS = ['geral', 'plano', 'wallet', 'router', 'vault', 'api'] as const;

export function ProfilePage() {
 const { t } = useTranslation('common');
 useTabRoute(activeTab, '/profile', 'geral', PROFILE_TAB_IDS);

 useEffect(() => {
 fetchProfile();
 loadPlanData(); // Load plan data early for feature-gated tabs
 const params = new URLSearchParams(window.location.search);
 const tab = params.get('tab');
 if (tab === 'wallet') activeTab.value = 'wallet';
 if (tab === 'plano') activeTab.value = 'plano';
 if (tab === 'integracoes') activeTab.value = 'integrations';
 // Handle OAuth callback results
 const oauth = params.get('oauth');
 if (oauth === 'success') {
 const provider = params.get('provider') || '';
 const user = params.get('user') || '';
 oauthMsg.value = { type: 'success', text: `${provider} conectado com sucesso${user ? ` (${user})` : ''}!` };
 activeTab.value = 'integrations';
 // Clean URL
 window.history.replaceState({}, '', '/profile/integrations');
 // Reload integrations
 setTimeout(() => fetchIntegrations(), 500);
 } else if (oauth === 'error') {
 const reason = params.get('reason') || 'unknown';
 oauthMsg.value = { type: 'error', text: `Erro ao conectar: ${reason}` };
 activeTab.value = 'integrations';
 window.history.replaceState({}, '', '/profile/integrations');
 }
 }, []);

 if (loading.value) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);
 if (!profile.value) return <div style={{ padding: 24, color: T.textSecondary }}>Perfil não encontrado</div>;

 const p = profile.value;

 return (
 <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
 {/* Header with avatar */}
 <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
 <div style={{
 width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #2F6BFF, #7C3AED)',
 display: 'flex', alignItems: 'center', justifyContent: 'center',
 fontSize: 24, fontWeight: 800, color: T.text, flexShrink: 0,
 }}>
 {getInitials(formName.value || p.name, p.email)}
 </div>
 <div>
 <h1 style={{ fontSize: 24, fontWeight: 800, color: T.text, margin: 0 }}>Meu Perfil</h1>
 <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 4 }}>{p.email}</div>
 <div style={{ fontSize: 11, color: 'rgba(167,179,199,0.5)', marginTop: 2 }}>
 Membro desde {formatDate(p.created_at)}
 </div>
 </div>
 </div>

 <TabBar />

 {activeTab.value === 'geral' && <TabGeral />}
 {activeTab.value === 'plano' && <TabPlano />}
 {activeTab.value === 'wallet' && <TabWallet />}
 {activeTab.value === 'router' && <TabRouterLLM />}
 {activeTab.value === 'vault' && <TabElonPassword />}
 {activeTab.value === 'api' && <TabAPI />}
 </div>
 );
}
